import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { isPushSupported, getPushStatus, subscribeToPush, unsubscribeFromPush } from '../utils/push';

// ── Push 4 (Session 21 — 26 May 2026): time helpers for member-facing
// scorecard views. Same shape as the admin helpers in useMatchState.js,
// duplicated here so this file has no admin-side dependency.

// 1-Hz tick. Pauses while tab is hidden. Used by LIVE tab and the active
// batter rows in SCORECARD so "minutes batted" ticks every second while
// the batter is at the crease.
function useNowTick() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let id = null;
    function start() { if (id == null) id = setInterval(() => setNow(new Date()), 1000); }
    function stop()  { if (id != null) { clearInterval(id); id = null; } }
    function onVis() {
      if (document.visibilityState === 'visible') { setNow(new Date()); start(); }
      else stop();
    }
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  return now;
}

// "Started h:mm AM/PM IST" — innings start time in Asia/Kolkata regardless
// of viewer timezone.
function fmtStartedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const t = d.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
  return `Started ${t} IST`;
}

// "h:mm AM/PM" — short time string for per-ball commentary timestamps.
function fmtShortTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// Elapsed time formatter: "MM:SS" up to an hour, "H:MM:SS" thereafter.
// `now` is a Date (or millis); `fromIso` is a TIMESTAMPTZ string.
function fmtElapsed(fromIso, now) {
  if (!fromIso) return '0:00';
  const startMs = new Date(fromIso).getTime();
  const nowMs   = (now instanceof Date ? now.getTime() : (now ?? Date.now()));
  let sec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const h = Math.floor(sec / 3600); sec -= h * 3600;
  const m = Math.floor(sec / 60);   sec -= m * 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

// "Minutes batted" for a batter, computed from this innings's active balls.
// Returns null if the batter never appeared on field.
//   - firstSeen = entered_at of the first ball where this batter was either
//     striker or non-striker
//   - lastSeen  = if dismissed: the ball where their dismissal happened;
//                  else if active: NOW (ticks)
//                  else (innings ended, batter not out): innings.ended_at
//                  else: last ball where they appeared as striker/non-striker
function batterMinutesAtCrease(myBalls, memberId, innings, nowMs) {
  if (!memberId || !Array.isArray(myBalls) || myBalls.length === 0) return null;

  let firstSeenMs = null;
  let lastAppearanceMs = null;
  let dismissedMs = null;

  for (const b of myBalls) {
    const isStriker  = b.striker_id === memberId;
    const isNonStrik = b.non_striker_id === memberId;
    const isDismissed = b.dismissed_player_id === memberId && !!b.dismissal_type;
    const tsRaw = b.created_at;  // /live-public aliases entered_at AS created_at
    if (!tsRaw) continue;
    const ts = new Date(tsRaw).getTime();
    if (!Number.isFinite(ts)) continue;

    if (isStriker || isNonStrik || isDismissed) {
      if (firstSeenMs == null || ts < firstSeenMs) firstSeenMs = ts;
      if (lastAppearanceMs == null || ts > lastAppearanceMs) lastAppearanceMs = ts;
    }
    if (isDismissed) {
      dismissedMs = ts;
    }
  }

  if (firstSeenMs == null) return null;

  let endMs;
  if (dismissedMs != null) {
    endMs = dismissedMs;
  } else if (innings && innings.status === 'live') {
    endMs = (nowMs ?? Date.now());
  } else if (innings && innings.ended_at) {
    endMs = new Date(innings.ended_at).getTime();
  } else {
    endMs = lastAppearanceMs;
  }

  let sec = Math.max(0, Math.floor((endMs - firstSeenMs) / 1000));
  const m = Math.floor(sec / 60);
  return m; // returns total minutes (integer)
}

// ─── MemberStatsPage ─────────────────────────────────────
export function MemberStatsPage() {
  const { memberId } = useParams();
  const { member: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [memberInfo, setMemberInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('batting');
  const [insightView, setInsightView] = useState('batting');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/stats/member/${memberId}`),
      api.get(`/members/${memberId}`),
    ]).then(([statsRes, memberRes]) => {
      setStats(statsRes.data);
      setMemberInfo(memberRes.data.member);
    }).finally(() => setLoading(false));
  }, [memberId]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!stats) return <div style={{ textAlign:'center', padding: 40, color:'#9ca3af' }}><div style={{ fontSize:'2rem', marginBottom:8 }}>🏏</div><div>No stats found for this member yet</div></div>;

  const isMe = memberId === me?.id;
  const bat  = stats.batting?.totals  || {};
  const bowl = stats.bowling?.totals  || {};
  const field = stats.fielding || {};
  const pts  = stats.points || {};

  const S = { // mini styles
    card:  { background:'white', border:'1px solid #e5e7eb', borderRadius:12, marginBottom:12, overflow:'hidden' },
    hdr:   { padding:'10px 14px', background:'#f8fafc', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.8rem', color:'#475569', letterSpacing:'0.08em', borderBottom:'1px solid #f1f5f9' },
    row:   { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 14px', borderBottom:'1px solid #f9fafb' },
    name:  { fontWeight:600, fontSize:'0.85rem', color:'#1E3A6E' },
    val:   { fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'#1E3A6E' },
    badge: (c) => ({ fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background: c+'20', color: c }),
  };

  const tabs = ['batting','bowling','fielding','insights'];

  return (
    <div style={{ paddingBottom:80 }}>
      {/* Header */}
      <div style={{ background:'#1E3A6E', color:'white', padding:'16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
          <div style={{ width:52, height:52, borderRadius:'50%', border:'3px solid #F5C518', background:'#2d4a8a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem', fontFamily:'Rajdhani,sans-serif', fontWeight:900, flexShrink:0 }}>
            {memberInfo?.display_name?.charAt(0) || '?'}
          </div>
          <div>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.2rem', color:'#F5C518' }}>
              {memberInfo?.display_name} {isMe && <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.5)' }}>(You)</span>}
            </div>
            <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.5)' }}>
              {memberInfo?.ucc_id}{memberInfo?.jersey_no ? ` · #${memberInfo.jersey_no}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
          {[
            { v: pts.total_points ? Math.round(parseFloat(pts.total_points)) : 0, l:'Points' },
            { v: pts.matches || 0, l:'Matches' },
            { v: pts.motm_count || 0, l:'MOTM 🏅' },
          ].map(s => (
            <div key={s.l} style={{ background:'rgba(255,255,255,0.1)', borderRadius:10, padding:'10px 8px', textAlign:'center' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.4rem', color:'#F5C518' }}>{s.v}</div>
              <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:'white', borderBottom:'1px solid #e5e7eb', position:'sticky', top:0, zIndex:10 }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{ flex:1, padding:'10px 4px', border:'none', borderBottom: activeTab===t ? '3px solid #1E3A6E' : '3px solid transparent',
              background:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.75rem',
              color: activeTab===t ? '#1E3A6E' : '#9ca3af', cursor:'pointer', textTransform:'uppercase', letterSpacing:'0.06em' }}>
            {t === 'batting' ? '🏏' : t === 'bowling' ? '🎳' : t === 'fielding' ? '🧤' : '💡'} {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      <div style={{ padding:'12px 16px' }}>

        {/* ── BATTING TAB ── */}
        {activeTab === 'batting' && (
          <>
            {/* Key stats grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
              {[
                { v: bat.total_runs||0, l:'Runs' },
                { v: bat.highest||0, l:'Highest' },
                { v: bat.innings > 0 ? (bat.total_runs / (bat.innings - (bat.not_outs||0)) || bat.total_runs).toFixed(1) : '—', l:'Average' },
                { v: bat.innings||0, l:'Innings' },
                { v: bat.fifties||0, l:'50s' },
                { v: bat.centuries||0, l:'100s' },
              ].map(s => (
                <div key={s.l} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 8px', textAlign:'center' }}>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem', color:'#1E3A6E' }}>{s.v}</div>
                  <div style={{ fontSize:'0.62rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Dismissal breakdown */}
            {stats.batting?.dismissal_breakdown?.length > 0 && (
              <div style={S.card}>
                <div style={S.hdr}>HOW I GET OUT</div>
                {stats.batting.dismissal_breakdown.map((r, i) => {
                  const icons = { caught:'🙌', bowled:'🎯', lbw:'📏', run_out:'🏃', stumped:'🧤', hit_wicket:'💥', caught_and_bowled:'🎯' };
                  const maxCount = stats.batting.dismissal_breakdown[0].count;
                  const barW = Math.round((r.count / maxCount) * 100);
                  return (
                    <div key={i} style={{ padding:'8px 14px', borderBottom:'1px solid #f9fafb' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:'0.8rem' }}>{icons[r.dismissal] || '🏏'}</span>
                        <span style={{ fontWeight:600, fontSize:'0.82rem', color:'#374151', flex:1, textTransform:'capitalize' }}>{r.dismissal?.replace(/_/g,' ')}</span>
                        <span style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:'#dc2626' }}>{r.count}</span>
                        <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>({r.percentage}%)</span>
                      </div>
                      <div style={{ height:6, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${barW}%`, background:'#dc2626', borderRadius:3 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* By batting position */}
            {stats.batting?.by_position?.length > 0 && (
              <div style={S.card}>
                <div style={S.hdr}>BY BATTING POSITION</div>
                {stats.batting.by_position.slice(0,5).map(r => (
                  <div key={r.batting_position} style={S.row}>
                    <span style={{ fontSize:'0.82rem', color:'#6b7280' }}>#{r.batting_position}</span>
                    <span style={S.name}>{r.innings} innings</span>
                    <span style={S.val}>{r.runs} runs</span>
                    <span style={{ fontSize:'0.75rem', color:'#9ca3af' }}>Avg {r.average}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── BOWLING TAB ── */}
        {activeTab === 'bowling' && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
              {[
                { v: bowl.total_wickets||0, l:'Wickets' },
                { v: bowl.economy||'—', l:'Economy' },
                { v: bowl.total_overs||0, l:'Overs' },
                { v: bowl.maidens||0, l:'Maidens' },
                { v: bowl.three_wkt_hauls||0, l:'3-Wkts' },
                { v: bowl.five_wkt_hauls||0, l:'5-Wkts' },
              ].map(s => (
                <div key={s.l} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 8px', textAlign:'center' }}>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem', color:'#16a34a' }}>{s.v}</div>
                  <div style={{ fontSize:'0.62rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.l}</div>
                </div>
              ))}
            </div>

            {stats.bowling?.best_spell && (
              <div style={{ ...S.card, border:'2px solid #16a34a' }}>
                <div style={{ ...S.hdr, background:'#f0fdf4', color:'#166534' }}>⭐ BEST SPELL</div>
                <div style={{ padding:'12px 14px', display:'flex', gap:16 }}>
                  {[
                    { v: `${stats.bowling.best_spell.wickets}/${stats.bowling.best_spell.runs}`, l:'Figures' },
                    { v: stats.bowling.best_spell.overs, l:'Overs' },
                    { v: parseFloat(stats.bowling.best_spell.economy||0).toFixed(2), l:'Economy' },
                  ].map(s => (
                    <div key={s.l} style={{ textAlign:'center', flex:1 }}>
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem', color:'#16a34a' }}>{s.v}</div>
                      <div style={{ fontSize:'0.65rem', color:'#9ca3af', textTransform:'uppercase' }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ padding:'0 14px 10px', fontSize:'0.72rem', color:'#6b7280' }}>
                  {new Date(stats.bowling.best_spell.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                </div>
              </div>
            )}

            {/* Batsmen I dismiss most */}
            {stats.bowling?.most_dismissed?.length > 0 && (
              <div style={S.card}>
                <div style={S.hdr}>🎯 BATSMEN I DISMISS MOST</div>
                {stats.bowling.most_dismissed.map((r, i) => (
                  <div key={i} style={S.row}>
                    <span style={S.name}>{r.display_name}</span>
                    <span style={S.badge('#16a34a')}>{r.wickets} wkt{r.wickets>1?'s':''}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Batsmen who score most against me */}
            {stats.bowling?.most_scored_against?.length > 0 && (
              <div style={S.card}>
                <div style={S.hdr}>💪 BATSMEN WHO SCORE MOST AGAINST ME</div>
                {stats.bowling.most_scored_against.map((r, i) => (
                  <div key={i} style={S.row}>
                    <span style={S.name}>{r.display_name}</span>
                    <span style={S.badge('#dc2626')}>{r.runs} runs</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── FIELDING TAB ── */}
        {activeTab === 'fielding' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
            {[
              { v: field.catches||0, l:'Catches', icon:'🙌' },
              { v: field.stumpings||0, l:'Stumpings', icon:'🧤' },
              { v: field.run_outs||0, l:'Run Outs', icon:'🏃' },
            ].map(s => (
              <div key={s.l} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'16px 8px', textAlign:'center' }}>
                <div style={{ fontSize:'1.5rem', marginBottom:4 }}>{s.icon}</div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.8rem', color:'#1E3A6E' }}>{s.v}</div>
                <div style={{ fontSize:'0.65rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── INSIGHTS TAB ── */}
        {activeTab === 'insights' && (
          <>
            {/* Section toggle */}
            <>
                  <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                    {[['batting','🏏 As Batter'],['bowling','🎳 As Bowler']].map(([v,l]) => (
                      <button key={v} onClick={() => setInsightView(v)}
                        style={{ flex:1, padding:'8px', borderRadius:10, border:'2px solid',
                          borderColor: insightView===v ? '#1E3A6E' : '#e5e7eb',
                          background: insightView===v ? '#1E3A6E' : 'white',
                          color: insightView===v ? 'white' : '#374151',
                          fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                        {l}
                      </button>
                    ))}
                  </div>

                  {insightView === 'batting' && (
                    <>
                      {/* Dismissal breakdown */}
                      {stats.batting?.dismissal_breakdown?.length > 0 && (
                        <div style={S.card}>
                          <div style={S.hdr}>📊 HOW I GET OUT</div>
                          <div style={{ padding:'10px 14px', display:'flex', flexWrap:'wrap', gap:8 }}>
                            {stats.batting.dismissal_breakdown.map((r, i) => {
                              const icons = { caught:'🙌', bowled:'🎯', lbw:'📏', run_out:'🏃', stumped:'🧤', hit_wicket:'💥' };
                              return (
                                <div key={i} style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:10, padding:'8px 12px', textAlign:'center', minWidth:75 }}>
                                  <div style={{ fontSize:'1rem', marginBottom:2 }}>{icons[r.dismissal]||'🏏'}</div>
                                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.1rem', color:'#dc2626' }}>{r.count}</div>
                                  <div style={{ fontSize:'0.6rem', color:'#6b7280', textTransform:'capitalize', marginBottom:1 }}>{r.dismissal?.replace(/_/g,' ')}</div>
                                  <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>{r.percentage}%</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Full vs bowler breakdown */}
                      {stats.batting?.vs_bowlers?.length > 0 ? (
                        <div style={S.card}>
                          <div style={S.hdr}>⚔️ MY RECORD VS EACH BOWLER</div>
                          <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
                              <thead>
                                <tr style={{ background:'#f8fafc' }}>
                                  <th style={{ padding:'7px 10px', textAlign:'left', fontSize:'0.68rem', color:'#94a3b8', fontWeight:700 }}>BOWLER</th>
                                  <th style={{ padding:'7px 6px', textAlign:'right', fontSize:'0.68rem', color:'#94a3b8', fontWeight:700 }}>FACED</th>
                                  <th style={{ padding:'7px 6px', textAlign:'right', fontSize:'0.68rem', color:'#16a34a', fontWeight:700 }}>RUNS</th>
                                  <th style={{ padding:'7px 6px', textAlign:'right', fontSize:'0.68rem', color:'#dc2626', fontWeight:700 }}>OUT</th>
                                  <th style={{ padding:'7px 8px', textAlign:'right', fontSize:'0.68rem', color:'#94a3b8', fontWeight:700 }}>AVG</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stats.batting.vs_bowlers.map((r, i) => {
                                  const isDanger = r.dismissals >= 2;
                                  const isStrong = r.runs_scored >= 20 && r.dismissals === 0;
                                  return (
                                    <tr key={i} style={{ borderBottom:'1px solid #f1f5f9', background: isDanger ? '#fff5f5' : isStrong ? '#f0fdf4' : 'white' }}>
                                      <td style={{ padding:'8px 10px', fontWeight:700, color:'#1E3A6E', fontSize:'0.82rem' }}>
                                        {r.bowler_name}
                                        {isDanger && <span style={{ marginLeft:4, fontSize:'0.6rem', color:'#dc2626' }}>⚠️ danger</span>}
                                        {isStrong && <span style={{ marginLeft:4, fontSize:'0.6rem', color:'#16a34a' }}>✅ strong</span>}
                                      </td>
                                      <td style={{ padding:'8px 6px', textAlign:'right', color:'#374151' }}>{r.times_faced}</td>
                                      <td style={{ padding:'8px 6px', textAlign:'right', fontWeight:700, color:'#16a34a' }}>{r.runs_scored}</td>
                                      <td style={{ padding:'8px 6px', textAlign:'right', fontWeight:700, color: r.dismissals > 0 ? '#dc2626' : '#9ca3af' }}>{r.dismissals}</td>
                                      <td style={{ padding:'8px 8px', textAlign:'right', color:'#6b7280', fontSize:'0.75rem' }}>{r.avg_vs || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div style={{ ...S.card, padding:16, textAlign:'center', color:'#9ca3af', fontSize:'0.82rem' }}>
                          ⚔️ Re-import scorecards to populate vs-bowler data
                        </div>
                      )}
                    </>
                  )}

                  {insightView === 'bowling' && (
                    <>
                      {/* Batsmen I dismiss most */}
                      {stats.bowling?.most_dismissed?.length > 0 ? (
                        <div style={S.card}>
                          <div style={S.hdr}>🎯 BATSMEN I DISMISS MOST</div>
                          {stats.bowling.most_dismissed.map((r, i) => (
                            <div key={i} style={{ ...S.row, background: i===0 ? '#f0fdf4' : 'white' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                {i===0 && <span style={{ fontSize:'0.8rem' }}>🏆</span>}
                                <div>
                                  <div style={S.name}>{r.display_name}</div>
                                  <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>{r.ucc_id}</div>
                                </div>
                              </div>
                              <span style={S.badge('#16a34a')}>{r.wickets} wkt{r.wickets>1?'s':''}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ ...S.card, padding:16, textAlign:'center', color:'#9ca3af', fontSize:'0.82rem' }}>
                          🎯 Re-import scorecards to populate bowling data
                        </div>
                      )}

                      {/* Batsmen who score most against me */}
                      {stats.bowling?.most_scored_against?.length > 0 && (
                        <div style={S.card}>
                          <div style={S.hdr}>💪 BATSMEN WHO SCORE MOST AGAINST ME</div>
                          {stats.bowling.most_scored_against.map((r, i) => (
                            <div key={i} style={{ ...S.row, background: i===0 ? '#fff5f5' : 'white' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                {i===0 && <span style={{ fontSize:'0.8rem' }}>⚠️</span>}
                                <div>
                                  <div style={S.name}>{r.display_name}</div>
                                  <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>{r.ucc_id}</div>
                                </div>
                              </div>
                              <span style={S.badge('#dc2626')}>{r.runs} runs</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Economy trend */}
                      {stats.bowling?.economy_trend?.length > 0 && (
                        <div style={S.card}>
                          <div style={S.hdr}>📈 ECONOMY BY MATCH</div>
                          <div style={{ padding:'10px 14px' }}>
                            {stats.bowling.economy_trend.map((r, i) => {
                              const econ = parseFloat(r.economy)||0;
                              const bar = Math.min(econ / 12 * 100, 100);
                              const color = econ < 6 ? '#16a34a' : econ < 8 ? '#d97706' : '#dc2626';
                              return (
                                <div key={i} style={{ marginBottom:8 }}>
                                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.7rem', color:'#6b7280', marginBottom:2 }}>
                                    <span>{new Date(r.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
                                    <span style={{ fontWeight:700, color }}>{econ.toFixed(2)} econ · {r.wickets}w/{r.overs}ov</span>
                                  </div>
                                  <div style={{ height:6, background:'#f1f5f9', borderRadius:3 }}>
                                    <div style={{ height:'100%', width:`${bar}%`, background:color, borderRadius:3 }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!isMe && (
                    <Link to={`/compare?m1=${me?.id}&m2=${memberId}`}
                      style={{ display:'block', background:'#1E3A6E', color:'white', borderRadius:12, padding:'12px 16px', textAlign:'center', textDecoration:'none', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', marginTop:4 }}>
                      ⚔️ Compare with Me
                    </Link>
                  )}
            </>
          </>
        )}
      </div>
    </div>
  );
}

// ─── HeadToHeadPage ───────────────────────────────────────
export function HeadToHeadPage() {
  const { member: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [m1, setM1] = useState(me?.id || '');
  const [m2, setM2] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/members').then(res => setMembers(res.data.members || []));
  }, []);

  async function compare() {
    if (!m1 || !m2 || m1 === m2) return toast.error('Select two different members');
    setLoading(true);
    try {
      const res = await api.get(`/stats/head-to-head?member1=${m1}&member2=${m2}`);
      setResult(res.data);
    } catch { toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  }

  const CATS = [
    { key: 'runs', label: 'Runs', better: 'higher' },
    { key: 'wickets', label: 'Wickets', better: 'higher' },
    { key: 'average', label: 'Average', better: 'higher' },
    { key: 'sr', label: 'Strike Rate', better: 'higher' },
    { key: 'economy', label: 'Economy', better: 'lower' },
    { key: 'dismissals', label: 'Fielding', better: 'higher' },
    { key: 'points', label: 'UCC Points', better: 'higher' },
    { key: 'motm', label: 'MOTM', better: 'higher' },
  ];

  return (
    <div>
      <div style={{ background: "white", borderBottom: "1px solid var(--border)", padding: "16px", position: "relative", overflow: "hidden" }}>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>⚔️ Head to Head</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Compare any two members</p>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        <div className="card mb-md">
          <div className="card__body">
            <div className="form-group">
              <label className="form-label">Player 1</label>
              <select className="form-control" value={m1} onChange={e => setM1(e.target.value)}>
                <option value="">Select player…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Player 2</label>
              <select className="form-control" value={m2} onChange={e => setM2(e.target.value)}>
                <option value="">Select player…</option>
                {members.filter(m => m.id !== m1).map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-block" onClick={compare} disabled={loading || !m1 || !m2}>
              {loading ? 'Comparing…' : '⚔️ Compare'}
            </button>
          </div>
        </div>

        {result && (
          <div className="card">
            <div className="card__header">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'space-around' }}>
                <div style={{ textAlign: 'center' }}>
                  <div className="avatar" style={{ margin: '0 auto 4px' }}>{result.member1?.display_name?.charAt(0)}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{result.member1?.display_name?.split(' ')[0]}</div>
                </div>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, color: 'var(--ucc-gray-400)' }}>VS</span>
                <div style={{ textAlign: 'center' }}>
                  <div className="avatar" style={{ margin: '0 auto 4px' }}>{result.member2?.display_name?.charAt(0)}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{result.member2?.display_name?.split(' ')[0]}</div>
                </div>
              </div>
            </div>
            <div className="card__body" style={{ padding: 0 }}>
              {CATS.map(cat => {
                const v1 = parseFloat(result.member1?.[cat.key] || 0);
                const v2 = parseFloat(result.member2?.[cat.key] || 0);
                const m1Wins = cat.better === 'higher' ? v1 > v2 : v1 < v2;
                const m2Wins = cat.better === 'higher' ? v2 > v1 : v2 < v1;
                return (
                  <div key={cat.key} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--ucc-gray-100)' }}>
                    <div style={{ flex: 1, textAlign: 'right', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: m1Wins ? '#1E3A6E' : 'var(--ucc-gray-400)' }}>{v1}</div>
                    <div style={{ width: 80, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--ucc-gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cat.label}</div>
                    <div style={{ flex: 1, textAlign: 'left', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: m2Wins ? '#1E3A6E' : 'var(--ucc-gray-400)' }}>{v2}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ScorecardsPage ───────────────────────────────────────
// `publicMode` renders this exact page at /club/scorecards for anonymous
// visitors. Year chips, match chips and the scorecard body are all shared code,
// so the public page cannot drift from the member one.
//
// Public reads go to /api/public/* mirrors of the member endpoints (same rows,
// same shape). The CricHeroes source view is not exposed publicly, so in public
// mode the source is pinned to the UCC App view and the toggle is hidden.
// ── Match video panel on the scorecard ───────────────────────
// The videos page links each video to its scorecard; this is the other way
// round. Uses the same video list (members' or public), loaded once per page
// visit, filtered to the match. Thumbnail first; the player loads on tap.
let _videoListPromise = null;
function loadVideoList(publicMode) {
  if (!_videoListPromise) {
    _videoListPromise = api.get(publicMode ? '/public/videos' : '/videos')
      .then(r => r.data.videos || []).catch(() => { _videoListPromise = null; return []; });
  }
  return _videoListPromise;
}
function MatchVideos({ matchId, publicMode }) {
  const [vids, setVids] = useState([]);
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    let alive = true;
    setPlaying(null);
    if (!matchId) { setVids([]); return undefined; }
    loadVideoList(publicMode).then(list => { if (alive) setVids(list.filter(v => v.match_id === matchId && v.video_id)); });
    return () => { alive = false; };
  }, [matchId, publicMode]);
  if (!vids.length) return null;
  return (
    <div className="container" style={{ paddingTop: 12 }}>
      {vids.map(v => (
        <div key={v.id} style={{ background: '#0f1e38', borderRadius: 14, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ position: 'relative', paddingTop: '56.25%' }}>
            {playing === v.video_id ? (
              <iframe title={v.title} src={`https://www.youtube.com/embed/${v.video_id}?autoplay=1&rel=0&playsinline=1`}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
            ) : (
              <button onClick={() => setPlaying(v.video_id)} aria-label={`Play ${v.title}`}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, padding: 0, cursor: 'pointer',
                         background: `#000 url(https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg) center/cover no-repeat` }}>
                <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 68, height: 48,
                               borderRadius: 12, background: 'rgba(220,38,38,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ width: 0, height: 0, borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
                                 borderLeft: '16px solid white', marginLeft: 4 }} />
                </span>
              </button>
            )}
          </div>
          <div style={{ padding: '10px 14px', color: 'white', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem' }}>🎥 {v.title}</span>
            <a href={v.youtube_url || `https://www.youtube.com/watch?v=${v.video_id}`} target="_blank" rel="noreferrer"
               style={{ color: '#F5C518', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>YouTube ↗</a>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ScorecardsPage({ publicMode = false }) {
  const { matchId } = useParams();
  const P = publicMode ? '/public' : '';
  const [matches, setMatches] = useState([]);
  const CURRENT_YEAR = new Date().getFullYear();
  const YEARS = Array.from({length: CURRENT_YEAR - 2013 + 1}, (_, i) => CURRENT_YEAR - i);
  const [year, setYear] = useState(matchId ? null : CURRENT_YEAR);
  const [selected, setSelected] = useState(matchId || null);
  const [scorecard, setScorecard] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [source, setSource] = useState('cricheroes'); // 'cricheroes' | 'ucc_app'
  const navigate = useNavigate();

  // When deep-linked to a specific match (e.g. from the Videos page), resolve
  // THAT match's year first so we load the right season — otherwise the default
  // year's list wouldn't contain it and the selection would fall back to the
  // latest match.
  useEffect(() => {
    if (matchId && year === null) {
      api.get(`${P}/matches/${matchId}`)
        .then(res => {
          const d = res.data?.match?.match_date;
          setYear(d ? new Date(d).getFullYear() : CURRENT_YEAR);
        })
        .catch(() => setYear(CURRENT_YEAR));
    }
  }, [matchId]); // eslint-disable-line

  useEffect(() => {
    if (year === null) return; // wait until the deep-linked match's year is resolved
    setLoading(true);
    api.get(`${P}/matches?past=true&year=${year}`).then(res => {
      const list = res.data.matches || [];
      setMatches(list);
      // If the currently-selected match isn't in this year's list, select the first of the year.
      if (list.length > 0 && !list.some(m => m.id === selected)) {
        setSelected(list[0].id);
      }
    }).finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (selected) loadScorecard(selected);
  }, [selected]);

  async function loadScorecard(id) {
    setScorecardLoading(true);
    try {
      if (publicMode) {
        // No public CricHeroes view — go straight to the UCC App scorecard.
        const mi = await api.get(`${P}/matches/${id}`);
        setMatchInfo(mi.data.match);
        setScorecard({});          // truthy, so the body below renders
        setSource('ucc_app');
      } else {
        const [sc, mi] = await Promise.all([api.get(`/matches/${id}/scorecard`), api.get(`/matches/${id}`)]);
        setScorecard(sc.data);
        setMatchInfo(mi.data.match);
        // The scorecard endpoint returns IMPORTED rows only, so an empty one
        // means there is nothing to show under CricHeroes — decided by the
        // rows, not by scoring_mode, which a match can carry wrongly (the
        // 27 Sept match stayed 'imported' while being scored live).
        const hasImported = (sc.data?.batting?.length || 0) + (sc.data?.bowling?.length || 0) > 0;
        const mode = mi.data.match?.scoring_mode;
        setSource(!hasImported || mode === 'live' ? 'ucc_app' : 'cricheroes');
      }
    } finally {
      setScorecardLoading(false);
    }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background: "white", borderBottom: "1px solid var(--border)", padding: "16px", position: "relative", overflow: "hidden", paddingBottom: 'var(--space-md)' }}>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif' }}>📋 Match Scorecards</h2>
      </div>

      {/* ── Year chips: pick a season, then a match below ── */}
      <div style={{ overflow: 'auto', background: 'white', borderBottom: '1px solid var(--ucc-gray-200)' }}>
        <div style={{ display: 'flex', padding: '8px', gap: 8, minWidth: 'max-content' }}>
          {YEARS.map(y => (
            <button key={y} onClick={() => setYear(y)}
              style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '2px solid', whiteSpace: 'nowrap',
                borderColor: year === y ? 'var(--gold)' : 'var(--ucc-gray-200)',
                background: year === y ? 'var(--gold)' : 'white',
                color: year === y ? 'white' : 'var(--ucc-gray-700)',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              {y}
            </button>
          ))}
        </div>
      </div>

      <div style={{ overflow: 'auto', background: 'white', borderBottom: '1px solid var(--ucc-gray-200)' }}>
        <div style={{ display: 'flex', padding: '8px', gap: 8, minWidth: 'max-content' }}>
          {matches.length === 0 && (
            <div style={{ padding: '8px 16px', color: 'var(--ucc-gray-500)', fontFamily: 'Rajdhani,sans-serif', fontWeight: 600 }}>
              No matches found for {year}.
            </div>
          )}
          {matches.map(m => (
            <button key={m.id} onClick={() => setSelected(m.id)}
              style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '2px solid', whiteSpace: 'nowrap',
                borderColor: selected === m.id ? '#1E3A6E' : 'var(--ucc-gray-200)',
                background: selected === m.id ? '#1E3A6E' : 'white',
                color: selected === m.id ? 'white' : 'var(--ucc-gray-700)',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              {new Date(m.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
              {m.series_name && ' 🏅'}
            </button>
          ))}
        </div>
      </div>

      {scorecardLoading ? <div className="loading-spinner"><div className="spinner" /></div> : scorecard && (
        <>
        {/* ── Source toggle: CricHeroes / UCC App ──
            Only shown when the match actually has imported (CricHeroes) rows.
            A match scored in the app has none, so the toggle is hidden and the
            view stays on UCC App (set as the default source above). */}
        {((scorecard?.batting?.length || 0) + (scorecard?.bowling?.length || 0) > 0) && !publicMode && (
        <div style={{ background: 'white', borderBottom: '1px solid var(--ucc-gray-200)', padding: '8px 16px', display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            onClick={() => setSource('cricheroes')}
            style={{
              padding: '6px 14px', borderRadius: 18, border: '2px solid',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
              cursor: 'pointer', whiteSpace: 'nowrap',
              borderColor: source === 'cricheroes' ? '#1E3A6E' : 'var(--ucc-gray-200)',
              background: source === 'cricheroes' ? '#1E3A6E' : 'white',
              color: source === 'cricheroes' ? 'white' : 'var(--ucc-gray-700)',
            }}
          >
            CricHeroes
          </button>
          <button
            onClick={() => setSource('ucc_app')}
            style={{
              padding: '6px 14px', borderRadius: 18, border: '2px solid',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
              cursor: 'pointer', whiteSpace: 'nowrap',
              borderColor: source === 'ucc_app' ? '#F5C518' : 'var(--ucc-gray-200)',
              background: source === 'ucc_app' ? '#F5C518' : 'white',
              color: source === 'ucc_app' ? '#1E3A6E' : 'var(--ucc-gray-700)',
            }}
          >
            UCC App {matchInfo?.scoring_mode === 'live' && '🔴'}
          </button>
        </div>
        )}

        <MatchVideos matchId={selected} publicMode={publicMode} />
        {source === 'ucc_app' ? (
          <UCCLiveView matchId={selected} matchInfo={matchInfo} publicMode={publicMode} />
        ) : (
        <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
          {matchInfo && (
            <div className="card mb-md" style={{ background: '#1E3A6E', color: 'white' }}>
              <div className="card__body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800 }}>
                      {(matchInfo.bat_first?.name ?? matchInfo.capt1_name)} ({(matchInfo.bat_first?.team ?? matchInfo.capt1_team) || 'Team A'})
                    </div>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '1.5rem', fontWeight: 800 }}>{(matchInfo.bat_first?.score ?? matchInfo.team_a_score) || '—'}</div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,.5)', fontFamily: 'Rajdhani,sans-serif', fontSize: '1.2rem' }}>VS</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800 }}>
                      {(matchInfo.bat_second?.name ?? matchInfo.capt2_name)} ({(matchInfo.bat_second?.team ?? matchInfo.capt2_team) || 'Team B'})
                    </div>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '1.5rem', fontWeight: 800 }}>{(matchInfo.bat_second?.score ?? matchInfo.team_b_score) || '—'}</div>
                  </div>
                </div>
                {matchInfo.winner_team && (
                  <div style={{ marginTop: 8, textAlign: 'center', color: 'var(--gold)', fontSize: '0.85rem', fontWeight: 700 }}>
                    🏆 {matchInfo.winner_side_team || matchInfo.winner_team} won
                    {matchInfo.margin && ` by ${matchInfo.margin} ${matchInfo.margin_type}`}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* The written match report — on this view too, not only under
              UCC App → Summary, since imported matches open on this tab. */}
          {matchInfo?.match_summary && (
            <div style={{ background: 'white', borderRadius: 14, padding: '14px 16px', marginBottom: 12,
                          border: '1px solid #e2e8f0', borderLeft: '4px solid #F5C518' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>🎙️ Match report</div>
              <div style={{ fontSize: '0.88rem', lineHeight: 1.65, color: '#334155', fontStyle: 'italic' }}>{matchInfo.match_summary}</div>
            </div>
          )}

          {(() => {
            const allInnings = [...new Set([
              ...scorecard.batting.map(b => b.innings),
              ...scorecard.bowling.map(b => b.innings)
            ])].sort();

            // bat_first / bat_second come from the API in batting order. The
            // captaincy slots are in fixture-creation order, which is not the
            // same thing — see GET /matches/:id.
            const firstSide  = matchInfo?.bat_first
              || { team: matchInfo?.capt1_team, name: matchInfo?.capt1_name };
            const secondSide = matchInfo?.bat_second
              || { team: matchInfo?.capt2_team, name: matchInfo?.capt2_name };
            const batTeam = (inn) => inn === 1
              ? (firstSide.team || firstSide.name || 'Team A')
              : (secondSide.team || secondSide.name || 'Team B');
            const bowlTeam = (inn) => inn === 1
              ? (secondSide.team || secondSide.name || 'Team B')
              : (firstSide.team || firstSide.name || 'Team A');

            const thStyle = (align) => ({
              padding: '7px 10px', textAlign: align, fontWeight: 700,
              fontSize: '0.7rem', color: '#64748b', background: '#f8fafc',
              borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap'
            });
            const tdStyle = (align, extra = {}) => ({
              padding: '7px 10px', textAlign: align,
              borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem',
              verticalAlign: 'middle', ...extra
            });

            return allInnings.map(inn => {
              const batRows = scorecard.batting.filter(b => b.innings === inn);
              const bowlRows = scorecard.bowling.filter(b => b.innings === inn);
              return (
                <React.Fragment key={inn}>
                  {/* ── BATTING ── */}
                  {batRows.length > 0 && (
                    <div className="card mb-md">
                      <div style={{ padding: '10px 14px', background: '#1E3A6E', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.08em' }}>
                          🏏 BATTING — {batTeam(inn).toUpperCase()}
                        </span>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thStyle('left')}>Batter</th>
                              <th style={thStyle('center')}>R</th>
                              <th style={thStyle('center')}>B</th>
                              <th style={thStyle('center')}>4s</th>
                              <th style={thStyle('center')}>6s</th>
                              <th style={thStyle('center')}>SR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batRows.map((b, i) => {
                              const isNotOut = b.dismissal === 'not_out' || b.dismissal === 'retired';
                              const howOut = isNotOut
                                ? 'not out'
                                : (b.dismissal_text || (b.dismissal ? b.dismissal.replace(/_/g, ' ') : '—'));
                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                                  <td style={tdStyle('left', { minWidth: 130, paddingTop: 7, paddingBottom: 7 })}>
                                    <Link to={b.member_id ? `/stats/${b.member_id}` : '#'}
                                      style={{ color: 'inherit', textDecoration: 'none' }}>
                                      <div style={{ fontWeight: 600, color: '#1E3A6E', fontSize: '0.88rem', lineHeight: 1.2 }}>
                                        {b.display_name || b.guest_name || '—'}
                                        {b.batting_hand && (
                                          <span style={{ fontSize: '0.62rem', color: '#94a3b8',
                                            marginLeft: 6, fontWeight: 500 }}>
                                            ({b.batting_hand})
                                          </span>
                                        )}
                                      </div>
                                    </Link>
                                    <div style={{ fontSize: '0.68rem', color: isNotOut ? '#16a34a' : '#64748b',
                                      marginTop: 2, lineHeight: 1.25, fontWeight: isNotOut ? 600 : 400 }}>
                                      {howOut}
                                    </div>
                                  </td>
                                  <td style={tdStyle('center', { fontWeight: 700, color: b.runs >= 50 ? '#16a34a' : '#1e293b' })}>{b.runs ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.balls ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.fours ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.sixes ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.strike_rate ? parseFloat(b.strike_rate).toFixed(1) : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── BOWLING ── */}
                  {bowlRows.length > 0 && (
                    <div className="card mb-md">
                      <div style={{ padding: '10px 14px', background: '#14532d', borderRadius: '10px 10px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#86efac', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.08em' }}>
                          🎳 BOWLING — {bowlTeam(inn).toUpperCase()}
                        </span>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thStyle('left')}>Bowler</th>
                              <th style={thStyle('center')}>O</th>
                              <th style={thStyle('center')}>M</th>
                              <th style={thStyle('center')}>R</th>
                              <th style={thStyle('center')}>W</th>
                              <th style={thStyle('center')}>Econ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bowlRows.map((b, i) => {
                              const econ = b.economy && parseFloat(b.economy) > 0
                                ? parseFloat(b.economy).toFixed(2)
                                : (b.overs && parseFloat(b.overs) > 0)
                                  ? (parseFloat(b.runs) / parseFloat(b.overs)).toFixed(2)
                                  : '—';
                              const econColor = parseFloat(econ) < 6 ? '#16a34a' : parseFloat(econ) < 9 ? '#d97706' : '#dc2626';
                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                                  <td style={tdStyle('left', { fontWeight: 600, color: '#1E3A6E', minWidth: 130 })}>
                                    <Link to={b.member_id ? `/stats/${b.member_id}` : '#'}
                                      style={{ color: 'inherit', textDecoration: 'none' }}>
                                      {b.display_name || b.guest_name || '—'}
                                    </Link>
                                  </td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.overs ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.maidens ?? '—'}</td>
                                  <td style={tdStyle('center', { color: '#64748b' })}>{b.runs ?? '—'}</td>
                                  <td style={tdStyle('center', { fontWeight: 700, color: b.wickets > 0 ? '#dc2626' : '#64748b' })}>{b.wickets ?? '—'}</td>
                                  <td style={tdStyle('center', { fontWeight: 600, color: econColor })}>{econ}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            });
          })()}
        </div>
        )}
        </>
      )}
    </div>
  );
}

// ─── UCCLiveView ──────────────────────────────────────────
// Member-side UCC App view. Routes to a different backend endpoint based on
// whether the match was scored live on UCC or imported from CricHeroes.
//   • LIVE match (scoring_mode='live'): polls /live-public every 5s while
//     an innings is live. Tabs: Live | Scorecard | Commentary | Partnerships.
//   • IMPORTED match (import_status='imported'): single fetch of
//     /imported-view. Tabs: Summary | Scorecard | Commentary | Partnerships.
// Live tab and Summary tab are exclusive — only one of them ever renders.
// `publicMode` renders this exact component for anonymous visitors at
// /club/scorecards/:id. Only two things change: the data comes from
// /api/public/match/:id (a pass-through of the same handlers, so the payload is
// identical), and member-only affordances are hidden. Everything else — tabs,
// tables, colours, spacing — is shared, so the public scorecard cannot drift
// from the member one.
export function UCCLiveView({ matchId, matchInfo, publicMode = false }) {
  const isImported = matchInfo?.import_status === 'imported';

  const [tab, setTab] = useState(isImported ? 'summary' : 'scorecard');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Push 4 (Session 21 — 26 May 2026): 1-Hz clock for live time displays
  // (innings elapsed, batter minutes at crease). Threaded down to LIVE and
  // SCORECARD tabs so they tick every second.
  const now = useNowTick();

  // Manual refresh — bumping refreshKey re-runs the effect, which calls load()
  // and clears refreshing flag in finally{}. The button is in the tab strip below.
  const handleRefresh = () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    setRefreshKey(k => k + 1);
  };

  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;
    let timer = null;
    // Local mirror of latest fetch — avoids the stale-closure bug where
    // setInterval captured `data` from the first render and stopped polling
    // once an innings ended (between-innings dead zone).
    let latest = null;
    let isFirstLoad = true;
    const endpoint = isImported ? 'imported-view' : 'live-public';
    // Public visitors have no token, so they read the same payload through the
    // anonymous route instead of the member scorer route.
    const url = publicMode
      ? `/public/match/${matchId}`
      : `/scorer/matches/${matchId}/${endpoint}`;

    async function load() {
      try {
        const r = await api.get(url);
        if (cancelled) return;
        latest = r.data;
        setData(r.data);
        setError(null);
        if (isFirstLoad) {
          isFirstLoad = false;
          if (isImported) {
            // Default to Summary on first load.
            setTab('summary');
          } else {
            const hasLive = r.data?.innings?.some(i => i.status === 'live');
            if (hasLive) setTab('live');
            else if (r.data?.summary) setTab('summary'); // completed live match
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load data');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }

    load();

    // Only LIVE matches poll. Imported data is frozen — single fetch is enough.
    if (!isImported) {
      timer = setInterval(() => {
        if (!latest?.innings) { load(); return; }
        const innings   = latest.innings;
        const anyLive   = innings.some(i => i.status === 'live');
        const expected  = (latest.match?.scoring_mode === 'live') ? 2 : innings.length;
        const allEnded  = innings.length >= expected && innings.every(i => i.status === 'completed' || i.status === 'ended');
        if (!allEnded || anyLive) load();
      }, 5000);
    }

    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [matchId, isImported, refreshKey]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (error) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <div style={{ background: '#fef3c7', color: '#92400e', padding: 14, borderRadius: 8, fontSize: '0.85rem', lineHeight: 1.5 }}>
          <strong>Couldn't load match data.</strong><br/>
          {error}<br/>
          <span style={{ fontSize: '0.78rem', opacity: 0.8 }}>Try the CricHeroes view above.</span>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { match, innings, live_state, balls, captaincy, squad, partnerships, summary, scorecard_figures, fall_of_wickets, figure_mins, match_summary } = data;
  const liveInnings = innings?.find(i => i.status === 'live');
  const showLiveTab = !isImported && !!liveInnings;
  const showSummaryTab = !!summary;

  // Empty state — only meaningful for live matches with no balls yet.
  if (!isImported && (!balls || balls.length === 0)) {
    const hasInnings = innings && innings.length > 0;
    return (
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <div className="card mb-md" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div className="card__body" style={{ padding: '18px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E', marginBottom: 6 }}>
              {hasInnings ? 'No balls recorded yet' : 'Not scored on the UCC app'}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.5 }}>
              {hasInnings
                ? 'The match has started but no balls have been recorded on the UCC app yet. Check back when the first ball is scored.'
                : 'This match wasn\'t scored on the UCC app. Its CricHeroes scorecard shows here once it has been imported — then use the CricHeroes tab above.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tab styling — symmetric, centred, compact
  const tabBtn = (active) => ({
    flex: 1, padding: '10px 4px', border: 0, borderBottom: '3px solid',
    borderBottomColor: active ? '#F5C518' : 'transparent',
    background: 'white',
    fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.74rem',
    color: active ? '#1E3A6E' : 'var(--ucc-gray-500)',
    cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    textAlign: 'center', whiteSpace: 'nowrap',
  });

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid var(--ucc-gray-200)' }}>
        {showLiveTab && (
          <button onClick={() => setTab('live')} style={tabBtn(tab === 'live')}>
            <span style={{ fontSize: '0.7rem' }}>🔴</span> Live
          </button>
        )}
        {showSummaryTab && (
          <button onClick={() => setTab('summary')} style={tabBtn(tab === 'summary')}>
            <span style={{ fontSize: '0.85rem' }}>🏆</span> Summary
          </button>
        )}
        <button onClick={() => setTab('scorecard')} style={tabBtn(tab === 'scorecard')}>
          <span style={{ fontSize: '0.85rem' }}>📋</span> Scorecard
        </button>
        <button onClick={() => setTab('commentary')} style={tabBtn(tab === 'commentary')}>
          <span style={{ fontSize: '0.85rem' }}>💬</span> Commentary
        </button>
        <button onClick={() => setTab('partnerships')} style={tabBtn(tab === 'partnerships')}>
          <span style={{ fontSize: '0.85rem' }}>🤝</span> P'ships
        </button>
        {/* Refresh button — pulls fresh data on demand */}
        <button onClick={handleRefresh} disabled={refreshing || loading}
          title="Refresh data"
          style={{
            flex: '0 0 auto', padding: '10px 12px', border: 0,
            borderBottom: '3px solid transparent', background: 'white',
            cursor: (refreshing || loading) ? 'wait' : 'pointer',
            opacity: (refreshing || loading) ? 0.55 : 1,
            fontSize: '0.95rem', color: '#1E3A6E',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <span style={{
            display: 'inline-block',
            animation: refreshing ? 'ucc-spin 0.8s linear infinite' : 'none',
          }}>🔄</span>
        </button>
      </div>
      {/* Inline keyframes for spinner (avoids needing a global stylesheet edit) */}
      <style>{`@keyframes ucc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {tab === 'live' && showLiveTab && (
        <UCCLiveTab match={match} innings={innings} liveInnings={liveInnings} liveState={live_state} balls={balls} squad={squad} captaincy={captaincy} now={now} />
      )}
      {tab === 'summary' && showSummaryTab && (
        <UCCSummaryTab match={match} innings={innings} summary={summary} captaincy={captaincy} matchSummary={match_summary} />
      )}
      {tab === 'scorecard' && (
        <UCCScorecardTab match={match} innings={innings} balls={balls} squad={squad} captaincy={captaincy} scorecardFigures={scorecard_figures} fallOfWickets={fall_of_wickets} figureMins={figure_mins || {}} now={now} publicMode={publicMode} />
      )}
      {tab === 'commentary' && (
        <UCCCommentaryTab innings={innings} balls={balls} />
      )}
      {tab === 'partnerships' && (
        <UCCPartnershipsTab innings={innings} partnerships={partnerships} balls={balls} captaincy={captaincy} match={match} />
      )}
    </div>
  );
}

// Helpers shared across tabs
function oversFromLegal(legal) {
  const ov = Math.floor(legal / 6);
  const rem = legal % 6;
  return `${ov}.${rem}`;
}
function teamNameForSide(match, side) {
  return side === 'A' ? (match.team_a_name || 'Side A') : (match.team_b_name || 'Side B');
}
function classifyBallLabel(b) {
  if (b.dismissal_type) return 'W';
  if (parseInt(b.wides, 10)   > 0) return parseInt(b.total_runs, 10) === 1 ? 'wd' : `${b.total_runs}wd`;
  if (parseInt(b.noballs, 10) > 0) return parseInt(b.total_runs, 10) === 1 ? 'nb' : `${b.total_runs}nb`;
  if (parseInt(b.byes, 10)    > 0) return `${b.byes}b`;
  if (parseInt(b.legbyes, 10) > 0) return `${b.legbyes}lb`;
  const r = parseInt(b.runs_off_bat || 0, 10);
  return String(r);
}
function ballColor(b) {
  if (b.dismissal_type) return '#dc2626';
  if (parseInt(b.runs_off_bat,10) === 6) return '#F5C518';
  if (parseInt(b.runs_off_bat,10) === 4) return '#1D4ED8';
  if (b.wides>0 || b.noballs>0 || b.byes>0 || b.legbyes>0) return '#B45309';
  if (parseInt(b.runs_off_bat,10) === 0) return '#9CA3AF';
  return '#111827';
}

function UCCLiveTab({ match, innings, liveInnings, liveState, balls, squad, captaincy, now }) {
  const lst = (liveState || []).find(s => s.innings_id === liveInnings.id);
  const battingTeam = teamNameForSide(match, liveInnings.batting_side);
  const bowlingTeam = teamNameForSide(match, liveInnings.bowling_side);

  const myBalls = balls.filter(b => b.innings_id === liveInnings.id);
  const maxOver = myBalls.reduce((m, b) => Math.max(m, b.over_number), 0);
  const thisOver = myBalls.filter(b => b.over_number === maxOver);
  const last6 = myBalls.slice(-6);

  // Find current striker / non-striker / bowler details
  const stId = lst?.striker_id;
  const nsId = lst?.non_striker_id;
  const bwId = lst?.bowler_id;
  const stName = balls.find(b => b.striker_id === stId)?.striker_name || '';
  const nsName = balls.find(b => b.non_striker_id === nsId)?.non_striker_name
              || balls.find(b => b.striker_id === nsId)?.striker_name || '';
  const bwName = balls.find(b => b.bowler_id === bwId)?.bowler_name || '';

  // Tally striker / bowler
  const tallyBatter = (id) => myBalls.filter(b => b.striker_id === id && !b.is_penalty).reduce((acc, b) => {
    // Balls faced = every delivery except a wide (no-balls count).
    if (!(parseInt(b.wides || 0, 10) > 0)) acc.balls++;
    acc.runs += parseInt(b.runs_off_bat || 0, 10);
    return acc;
  }, { runs: 0, balls: 0 });
  const tallyBowler = (id) => myBalls.filter(b => b.bowler_id === id).reduce((acc, b) => {
    if (b.is_legal) acc.legal++;
    acc.runs += parseInt(b.runs_off_bat||0,10) + parseInt(b.wides||0,10) + parseInt(b.noballs||0,10);
    if (b.dismissal_type && ['bowled','caught','lbw','stumped','hit_wicket','caught_behind'].includes(b.dismissal_type)) acc.wkts++;
    return acc;
  }, { legal: 0, runs: 0, wkts: 0 });
  const stFig = stId ? tallyBatter(stId) : null;
  const nsFig = nsId ? tallyBatter(nsId) : null;
  const bwFig = bwId ? tallyBowler(bwId) : null;

  // Push 4: per-batter time at crease (minutes), live-ticking for active batters.
  const nowMs   = (now instanceof Date) ? now.getTime() : Date.now();
  const stMins  = stId ? batterMinutesAtCrease(myBalls, stId, liveInnings, nowMs) : null;
  const nsMins  = nsId ? batterMinutesAtCrease(myBalls, nsId, liveInnings, nowMs) : null;

  const targetLine = liveInnings.target_runs
    ? `Target ${liveInnings.target_runs} · need ${Math.max(0, liveInnings.target_runs - liveInnings.total_runs)} from ${(liveInnings.max_overs * 6) - liveInnings.legal_balls} balls`
    : null;

  return (
    <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
      {/* Score block */}
      <div className="card mb-md" style={{ background: '#1E3A6E', color: 'white' }}>
        <div className="card__body">
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
            {battingTeam} batting · vs {bowlingTeam}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div style={{ fontSize: '2.4rem', fontWeight: 800, fontFamily: 'Rajdhani,sans-serif', lineHeight: 1 }}>
              {liveInnings.total_runs}<span style={{ color: '#F5C518' }}>/{liveInnings.wickets}</span>
            </div>
            <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)' }}>
              ({oversFromLegal(liveInnings.legal_balls)} of {liveInnings.max_overs})
            </div>
          </div>
          {targetLine && (
            <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#F5C518', fontWeight: 600 }}>
              {targetLine}
            </div>
          )}
          {lst?.pending_free_hit && (
            <div style={{ display: 'inline-block', marginTop: 6, padding: '2px 8px', background: '#dc2626', borderRadius: 12, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em' }}>
              FREE HIT
            </div>
          )}
          {/* Push 4 (Session 21 — 26 May 2026): innings start time + live elapsed. */}
          {liveInnings.started_at && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)', fontVariantNumeric: 'tabular-nums' }}>
              <span>{fmtStartedAt(liveInnings.started_at)}</span>
              <span style={{ color: '#F5C518', fontWeight: 700 }}>
                Elapsed {fmtElapsed(liveInnings.started_at, now)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Current players */}
      <div className="card mb-md">
        <div className="card__body" style={{ padding: '12px 14px' }}>
          {stId && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dashed var(--ucc-gray-200)', fontSize: '0.88rem' }}>
              <span style={{ fontWeight: 600 }}>
                ● {stName} *
                {stMins != null && (
                  <span style={{ marginLeft: 6, color: '#64748b', fontWeight: 500, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                    · {stMins}m
                  </span>
                )}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#1E3A6E', fontWeight: 700 }}>{stFig.runs} ({stFig.balls})</span>
            </div>
          )}
          {nsId && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dashed var(--ucc-gray-200)', fontSize: '0.88rem' }}>
              <span>
                ○ {nsName}
                {nsMins != null && (
                  <span style={{ marginLeft: 6, color: '#64748b', fontWeight: 500, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                    · {nsMins}m
                  </span>
                )}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#64748b', fontWeight: 600 }}>{nsFig.runs} ({nsFig.balls})</span>
            </div>
          )}
          {bwId && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0', fontSize: '0.85rem' }}>
              <span style={{ color: '#1E3A6E', fontWeight: 700 }}>↪ {bwName}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                {oversFromLegal(bwFig.legal)}–0–{bwFig.runs}–{bwFig.wkts}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* This over */}
      {thisOver.length > 0 && (
        <div className="card mb-md">
          <div className="card__body" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 6 }}>
              This over (#{maxOver + 1})
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {thisOver.map(b => (
                <div key={b.id} style={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: ballColor(b) === '#9CA3AF' ? '#f3f4f6' : ballColor(b),
                  color: ballColor(b) === '#9CA3AF' ? '#9CA3AF' : (ballColor(b) === '#F5C518' ? '#1E3A6E' : 'white'),
                  fontSize: '0.78rem', fontWeight: 800, border: '1px solid #d1d5db',
                }}>
                  {classifyBallLabel(b)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Last 6 balls (broader recent feed) */}
      {last6.length > 0 && (
        <div className="card mb-md">
          <div className="card__body" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 6 }}>
              Last 6 balls
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {last6.map(b => (
                <div key={b.id} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: ballColor(b) === '#9CA3AF' ? '#f3f4f6' : ballColor(b),
                  color: ballColor(b) === '#9CA3AF' ? '#9CA3AF' : (ballColor(b) === '#F5C518' ? '#1E3A6E' : 'white'),
                  fontSize: '0.72rem', fontWeight: 800, border: '1px solid #d1d5db',
                }}>
                  {classifyBallLabel(b)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── InningsToggle ─────────────────────────────────────────
// Pill-style innings switcher used across Scorecard / Commentary / P'ships.
// Selected pill shows in navy with gold text. Disabled pills render gray.
function InningsToggle({ innings, value, onChange }) {
  if (!Array.isArray(innings) || innings.length === 0) return null;
  return (
    <div style={{
      display: 'flex', gap: 6, padding: '10px 12px 4px',
      justifyContent: 'center', alignItems: 'center',
    }}>
      {innings.slice().sort((a, b) => a.innings_number - b.innings_number).map(inn => {
        const active = inn.innings_number === value;
        return (
          <button
            key={inn.innings_number}
            onClick={() => onChange(inn.innings_number)}
            style={{
              padding: '6px 16px',
              border: active ? '1px solid #1E3A6E' : '1px solid #cbd5e1',
              background: active ? '#1E3A6E' : 'white',
              color: active ? '#F5C518' : '#475569',
              borderRadius: 999,
              fontSize: '0.75rem', fontWeight: 700,
              fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.06em',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}>
            INN {inn.innings_number}
            {inn.batting_team && (
              <span style={{ marginLeft: 4, opacity: 0.85, fontWeight: 600 }}>
                · {inn.batting_team.length > 12 ? inn.batting_team.slice(0,12) + '…' : inn.batting_team}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function UCCScorecardTab({ match, innings, balls, squad, captaincy, scorecardFigures, fallOfWickets, figureMins, now, publicMode = false }) {
  // For imported matches the backend supplies pre-aggregated figures pulled
  // directly from batting_figures + bowling_figures. For live matches we
  // derive them from the ball-by-ball feed. The render path below is the
  // same — only the source data differs.
  const useFigures = Array.isArray(scorecardFigures) && scorecardFigures.length > 0;

  // Keeper member-ids (both sides) for caught-behind dagger on the live card.
  // On a caught dismissal the fielder is from the bowling side, so membership
  // in this set means the catcher was the keeper → caught behind.
  const keeperIds = new Set((squad || []).filter(s => s.is_keeper).map(s => s.member_id));

  // Player drill-down modal (tap a batter/bowler name).
  const [drill, setDrill] = useState(null); // member_id | null

  // Innings toggle: default to innings 1.
  const [activeInn, setActiveInn] = useState(1);

  // Derive batting + bowling figures per innings from active balls
  function batterFigures(myBalls) {
    const map = new Map();
    for (const b of myBalls) {
      const sid = b.striker_id;
      if (!sid) continue;
      if (!map.has(sid)) map.set(sid, { id: sid, name: b.striker_name || '—', runs: 0, balls: 0, fours: 0, sixes: 0, dismissal: null, dismissalText: null });
      const e = map.get(sid);
      // Balls faced = every delivery except a wide (no-balls count); penalty rows are not balls.
      if (!b.is_penalty && !(parseInt(b.wides || 0, 10) > 0)) e.balls++;
      const r = parseInt(b.runs_off_bat || 0, 10);
      e.runs += r;
      if (r === 4) e.fours++;
      if (r === 6) e.sixes++;
      // Mark dismissal if this batter was dismissed on this ball
      if (b.dismissal_type && b.dismissed_player_id === sid) {
        e.dismissal = b.dismissal_type;
        e.dismissalText = formatDismissal(b);
      }
      // Also mark if the dismissed player was the non-striker (run-out)
      if (b.dismissal_type && b.dismissed_player_id === b.non_striker_id) {
        // Will be picked up when that batter takes strike — but if they never bat
        // again this innings, we miss it. Handle below.
      }
    }
    // Pick up non-striker dismissals (run-out) — add them as separate entries
    for (const b of myBalls) {
      if (b.dismissal_type && b.dismissed_player_id && b.dismissed_player_id !== b.striker_id) {
        const did = b.dismissed_player_id;
        if (!map.has(did)) {
          map.set(did, {
            id: did,
            name: b.dismissed_name || b.non_striker_name || '—',
            runs: 0, balls: 0, fours: 0, sixes: 0,
            dismissal: b.dismissal_type,
            dismissalText: formatDismissal(b),
          });
        } else {
          const e = map.get(did);
          if (!e.dismissal) {
            e.dismissal = b.dismissal_type;
            e.dismissalText = formatDismissal(b);
          }
        }
      }
    }
    return Array.from(map.values());
  }

  function bowlerFigures(myBalls) {
    const map = new Map();
    for (const b of myBalls) {
      const bid = b.bowler_id;
      if (!bid) continue;
      if (!map.has(bid)) map.set(bid, { id: bid, name: b.bowler_name || '—', legal: 0, runs: 0, wkts: 0, maidens: 0, overs: new Map() });
      const e = map.get(bid);
      const charged = parseInt(b.runs_off_bat||0,10) + parseInt(b.wides||0,10) + parseInt(b.noballs||0,10);
      if (b.is_legal) e.legal++;
      e.runs += charged;
      if (b.dismissal_type && ['bowled','caught','lbw','stumped','hit_wicket','caught_behind'].includes(b.dismissal_type)) e.wkts++;
      // Per-over tally for maidens (byes / leg-byes don't spoil a maiden).
      if (!b.is_penalty) {
        const ov = e.overs.get(b.over_number) || { legal: 0, runs: 0 };
        if (b.is_legal) ov.legal++;
        ov.runs += charged;
        e.overs.set(b.over_number, ov);
      }
    }
    // Maiden = this bowler bowled all 6 legal balls of the over and conceded
    // nothing (same rule as the stored figures in ballDerivation.js).
    return Array.from(map.values()).map(e => {
      let maidens = 0;
      for (const ov of e.overs.values()) if (ov.legal === 6 && ov.runs === 0) maidens++;
      const out = { ...e, maidens };
      delete out.overs;
      return out;
    });
  }

  function formatDismissal(b) {
    // Prefer the authoritative stored text when present: it already includes
    // BOTH fielders on a two-fielder run-out and the keeper dagger. Only rebuild
    // from parts when no stored text is available (e.g. an in-progress ball).
    if (b.dismissal_text) return b.dismissal_text;
    const t = b.dismissal_type;
    const fielder = b.fielder_name;
    const fielder2 = b.fielder2_name || b.dismissal_fielder2_name;
    const bowler = b.bowler_name;
    if (t === 'caught' && fielder) {
      const dagger = (b.dismissal_fielder_id && keeperIds.has(b.dismissal_fielder_id)) ? '†' : '';
      return `c ${dagger}${fielder} b ${bowler}`;
    }
    if (t === 'caught_behind' && fielder) return `c †${fielder} b ${bowler}`;
    if (t === 'bowled') return `b ${bowler}`;
    if (t === 'lbw') return `lbw b ${bowler}`;
    if (t === 'stumped' && fielder) return `st ${fielder} b ${bowler}`;
    if (t === 'run_out') {
      const names = [fielder, fielder2].filter(Boolean).join('/');
      return names ? `run out (${names})` : 'run out';
    }
    if (t === 'hit_wicket') return `hit wicket b ${bowler}`;
    if (t === 'retired_out') return 'retired out';
    return t.replace(/_/g, ' ');
  }

  function oversFromDecimal(o) {
    if (o == null) return '—';
    const n = Number(o);
    if (!Number.isFinite(n) || n < 0) return '—';
    const full = Math.floor(n);
    const tenths = Math.round((n - full) * 10);
    return `${full}.${Math.max(0, Math.min(5, tenths))}`;
  }

  // Fall of Wickets: walk the innings' balls and emit a row at every wicket.
  // Live matches carry per-ball team_score_after / team_wickets_after snapshots;
  // imported matches don't, so we accumulate a running team total on the fly.
  function deriveFoW(innBalls) {
    const out = [];
    const sorted = [...innBalls].sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    let runRunning = 0, wktRunning = 0;
    for (const b of sorted) {
      runRunning += (Number(b.runs_off_bat) || 0) + (Number(b.wides) || 0) + (Number(b.noballs) || 0)
                  + (Number(b.byes) || 0) + (Number(b.legbyes) || 0);
      if (!b.dismissal_type) continue;
      wktRunning += 1;
      out.push({
        team_score:   b.team_score_after   != null ? b.team_score_after   : runRunning,
        team_wickets: b.team_wickets_after != null ? b.team_wickets_after : wktRunning,
        dismissed_name: b.dismissed_name || '—',
        over_label:   b.display_label || `${b.over_number}.${b.ball_in_over}`,
      });
    }
    return out;
  }

  const thStyle = (align) => ({
    padding: '7px 10px', textAlign: align, fontWeight: 700,
    fontSize: '0.7rem', color: '#64748b', background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
  });
  const tdStyle = (align, extra = {}) => ({
    padding: '7px 10px', textAlign: align,
    borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem',
    verticalAlign: 'middle', ...extra,
  });

  // Determine per-innings team names. For imported matches we use the
  // batting_team / bowling_team strings the backend computed; for live
  // matches we fall back to teamNameForSide.
  function innBattingTeam(inn) {
    if (inn.batting_team) return inn.batting_team;
    return teamNameForSide(match, inn.batting_side);
  }
  function innBowlingTeam(inn) {
    if (inn.bowling_team) return inn.bowling_team;
    return teamNameForSide(match, inn.bowling_side);
  }

  // Visible innings = only the one selected by the toggle.
  const visibleInnings = (innings || []).filter(i => i.innings_number === activeInn);

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xs)' }}>
      <InningsToggle innings={innings} value={activeInn} onChange={setActiveInn} />
      {visibleInnings.map(inn => {
        // Pick the figures source: pre-aggregated for imported, derived for live
        let batRows, bwlRows;
        if (useFigures) {
          const figs = scorecardFigures.find(f => f.innings_number === inn.innings_number);
          batRows = (figs?.batting || []).map(r => ({
            id:           r.member_id || `guest-${r.name}`,
            name:         r.name,
            runs:         r.runs,
            balls:        r.balls,
            fours:        r.fours,
            sixes:        r.sixes,
            strike_rate:  r.strike_rate,
            dismissal:    r.dismissal,
            dismissalText: r.dismissal_text,
            batting_hand: r.batting_hand,
            minutes:      r.minutes,
          }));
          bwlRows = (figs?.bowling || []).map(r => ({
            id:        r.member_id || `guest-${r.name}`,
            name:      r.name,
            overs:     r.overs,
            maidens:   r.maidens,
            runs:      r.runs,
            wkts:      r.wickets,
            economy:   r.economy,
          }));
        } else {
          const myBalls = balls.filter(b => b.innings_id === inn.id);
          if (myBalls.length === 0) return null;
          batRows = batterFigures(myBalls);
          bwlRows = bowlerFigures(myBalls);

          // Enrich live-mode batter rows with minutes + batting_hand.
          // Priority: stored figure_mins (backfilled from CricHeroes) > computed from ball timestamps.
          // Computed timestamps inflate on multi-day matches, so stored is preferred.
          const nowMs = (now instanceof Date) ? now.getTime() : Date.now();
          batRows = batRows.map(r => {
            const stored = (figureMins || {})[r.id];
            const computedMins = batterMinutesAtCrease(myBalls, r.id, inn, nowMs);
            const minutes = stored?.minutes != null ? stored.minutes : computedMins;
            const batting_hand = r.batting_hand || stored?.batting_hand || null;
            return { ...r, minutes, batting_hand };
          });
        }

        if ((!batRows || batRows.length === 0) && (!bwlRows || bwlRows.length === 0)) return null;

        const battingTeam = innBattingTeam(inn);
        const bowlingTeam = innBowlingTeam(inn);
        const extras = (inn.wides_total || 0) + (inn.noballs_total || 0) + (inn.byes_total || 0) + (inn.legbyes_total || 0) + (inn.penalty_total || 0);

        // Fall of Wickets: backend-provided for completed matches, else derived
        // live from this innings' balls.
        const fowForInn = (Array.isArray(fallOfWickets) && fallOfWickets.some(w => Number(w.innings) === Number(inn.innings_number)))
          ? fallOfWickets.filter(w => Number(w.innings) === Number(inn.innings_number))
          : deriveFoW(balls.filter(b => b.innings_id === inn.id));

        // Yet to bat: batting-side squad members who have not batted yet.
        const battedIds = new Set((batRows || []).map(r => r.id));
        const yetToBat = (squad || [])
          .filter(s => s.side === inn.batting_side && !battedIds.has(s.member_id))
          .map(s => s.display_name || s.name)
          .filter(Boolean);

        return (
          <React.Fragment key={inn.id}>
            <div className="card mb-md">
              <div style={{ padding: '10px 14px', background: '#1E3A6E', borderRadius: '10px 10px 0 0' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.08em' }}>
                  🏏 BATTING — {battingTeam.toUpperCase()} ({inn.total_runs}/{inn.wickets} in {oversFromLegal(inn.legal_balls)})
                </span>
              </div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: 36 }} />
                    <col style={{ width: 36 }} />
                    <col style={{ width: 36 }} />
                    <col style={{ width: 36 }} />
                    <col style={{ width: 36 }} />
                    <col style={{ width: 52 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle('left')}>Batter</th>
                      <th style={thStyle('center')}>R</th>
                      <th style={thStyle('center')}>B</th>
                      <th style={thStyle('center')} title="Minutes batted">M</th>
                      <th style={thStyle('center')}>4s</th>
                      <th style={thStyle('center')}>6s</th>
                      <th style={thStyle('center')}>SR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batRows.map((b, i) => {
                      // Compute SR if not provided (live mode); for imported it comes from DB.
                      const srNum = (b.strike_rate != null)
                        ? Number(b.strike_rate)
                        : (b.balls > 0 ? (b.runs / b.balls) * 100 : null);
                      const sr = srNum != null ? srNum.toFixed(1) : '—';
                      const srColor = srNum == null ? '#64748b'
                        : srNum < 80 ? '#dc2626'
                        : srNum < 100 ? '#d97706'
                        : '#16a34a';
                      // A non-empty string 'not_out' is truthy, so we can't
                      // just do `!!b.dismissal` — we must check the value.
                      // Retired (any flavour: retired_hurt, retired_out) is
                      // treated as "not out" for display, mirroring the
                      // imported-scorecard renderer above.
                      const isOut = !!b.dismissal
                        && b.dismissal !== 'not_out'
                        && !String(b.dismissal).startsWith('retired');
                      const dismissalLine = isOut
                        ? (b.dismissalText || 'out')
                        : 'not out';
                      return (
                        <tr key={`${b.id}-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                          <td style={tdStyle('left', { paddingTop: 6, paddingBottom: 6 })}>
                            <div style={{ fontWeight: 600, color: '#1E3A6E', fontSize: '0.85rem', lineHeight: 1.2, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                              {b.id && !String(b.id).startsWith('guest-')
                                ? <span onClick={() => setDrill(b.id)} style={{ cursor: 'pointer', borderBottom: '1px dotted #94a3b8' }}>{b.name}</span>
                                : b.name}
                              {b.batting_hand && (
                                <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>
                                  {b.batting_hand}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.67rem', color: isOut ? '#64748b' : '#16a34a', marginTop: 1, lineHeight: 1.25, fontWeight: isOut ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {dismissalLine}
                            </div>
                          </td>
                          <td style={tdStyle('center', { fontWeight: 700, color: b.runs >= 50 ? '#16a34a' : '#1e293b', fontSize: '0.85rem' })}>{b.runs}</td>
                          <td style={tdStyle('center', { color: '#64748b', fontSize: '0.82rem' })}>{b.balls}</td>
                          <td style={tdStyle('center', { color: '#64748b', fontSize: '0.82rem' })}>{b.minutes != null ? b.minutes : '—'}</td>
                          <td style={tdStyle('center', { color: '#64748b', fontSize: '0.82rem' })}>{b.fours}</td>
                          <td style={tdStyle('center', { color: '#64748b', fontSize: '0.82rem' })}>{b.sixes}</td>
                          <td style={tdStyle('center', { fontWeight: 600, color: srColor, fontSize: '0.82rem' })}>{sr}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Yet to bat — real list for live (from squad); imported has no squad. */}
              {(yetToBat.length > 0 || useFigures) && (
                <div style={{ padding: '6px 14px', fontSize: '0.74rem', color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontWeight: 700, color: '#94a3b8' }}>Yet to bat: </span>
                  {yetToBat.length > 0 ? yetToBat.join(', ') : '—'}
                </div>
              )}
              <div style={{ padding: '8px 14px', fontSize: '0.78rem', color: '#64748b', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                <span>Extras: {extras} (wd {inn.wides_total || 0}, nb {inn.noballs_total || 0}, b {inn.byes_total || 0}, lb {inn.legbyes_total || 0}{(inn.penalty_total || 0) > 0 ? `, pen ${inn.penalty_total}` : ''})</span>
                <span style={{ fontWeight: 700, color: '#1E3A6E' }}>
                  Total {inn.total_runs}/{inn.wickets} ({oversFromLegal(inn.legal_balls)} ov)
                </span>
              </div>
              {fowForInn.length > 0 && (
                <div style={{ padding: '8px 12px 10px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 6, textTransform: 'uppercase' }}>
                    🪦 Fall of Wickets
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {fowForInn.map((w, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        background: 'white', border: '1px solid #e2e8f0',
                        padding: '3px 8px', borderRadius: 999,
                        fontSize: '0.7rem', whiteSpace: 'nowrap',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}>
                        <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, color: '#1E3A6E', letterSpacing: '0.02em' }}>
                          {w.team_score}<span style={{ color: '#F5C518' }}>-{w.team_wickets}</span>
                        </span>
                        <span style={{ color: '#94a3b8', fontSize: '0.6rem' }}>·</span>
                        <span style={{ color: '#334155', fontWeight: 500, fontSize: '0.68rem' }}>{w.dismissed_name}</span>
                        <span style={{ color: '#cbd5e1', fontSize: '0.6rem' }}>·</span>
                        <span style={{ color: '#94a3b8', fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, fontSize: '0.66rem' }}>{w.over_label}ov</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="card mb-md">
              <div style={{ padding: '10px 14px', background: '#14532d', borderRadius: '10px 10px 0 0' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#86efac', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.08em' }}>
                  🎳 BOWLING — {bowlingTeam.toUpperCase()}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle('left')}>Bowler</th>
                      <th style={thStyle('center')}>O</th>
                      <th style={thStyle('center')}>M</th>
                      <th style={thStyle('center')}>R</th>
                      <th style={thStyle('center')}>W</th>
                      <th style={thStyle('center')}>Econ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bwlRows.map((b, i) => {
                      let overs, econ;
                      if (useFigures) {
                        overs = oversFromDecimal(b.overs);
                        econ = (b.economy != null) ? Number(b.economy).toFixed(2)
                              : (b.overs && Number(b.overs) > 0 ? (b.runs / Number(b.overs)).toFixed(2) : '—');
                      } else {
                        overs = oversFromLegal(b.legal);
                        const oversDec = b.legal > 0 ? b.legal / 6 : 0;
                        econ = oversDec > 0 ? (b.runs / oversDec).toFixed(2) : '—';
                      }
                      const econNum = parseFloat(econ);
                      const econColor = Number.isFinite(econNum)
                        ? (econNum < 9 ? '#16a34a' : econNum === 9 ? '#d97706' : '#dc2626')
                        : '#64748b';
                      return (
                        <tr key={`${b.id}-${i}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                          <td style={tdStyle('left', { fontWeight: 600, color: '#1E3A6E', minWidth: 130 })}>
                            {b.id && !String(b.id).startsWith('guest-')
                              ? <span onClick={() => setDrill(b.id)} style={{ cursor: 'pointer', borderBottom: '1px dotted #94a3b8' }}>{b.name}</span>
                              : b.name}
                          </td>
                          <td style={tdStyle('center', { color: '#64748b' })}>{overs}</td>
                          <td style={tdStyle('center', { color: '#64748b' })}>{b.maidens ?? 0}</td>
                          <td style={tdStyle('center', { color: '#64748b' })}>{b.runs}</td>
                          <td style={tdStyle('center', { fontWeight: 700, color: b.wkts > 0 ? '#dc2626' : '#64748b' })}>{b.wkts}</td>
                          <td style={tdStyle('center', { fontWeight: 600, color: econColor })}>{econ}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {drill && <PlayerBreakdownModal matchId={match?.id} memberId={drill} onClose={() => setDrill(null)} publicMode={publicMode} />}
    </div>
  );
}

// ─── PlayerBreakdownModal ─────────────────────────────────
// Per-player, per-match drill-down. Fetches the breakdown endpoint and shows
// batting / bowling / fielding sections (by role) with points and a
// ball-by-ball sequence.
function PlayerBreakdownModal({ matchId, memberId, onClose, publicMode = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [openBd, setOpenBd] = useState({});
  const toggleBd = (k) => setOpenBd(s => ({ ...s, [k]: !s[k] }));

  useEffect(() => {
    let alive = true;
    setLoading(true); setLoadErr(null);
    api.get(publicMode
      ? `/public/match/${matchId}/player/${memberId}/breakdown`
      : `/scorer/matches/${matchId}/player/${memberId}/breakdown`)
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setLoadErr(e?.response?.data?.error || 'Failed to load'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [matchId, memberId]);

  const Row = ({ label, value }) => (value == null || value === '' ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ fontWeight: 600, color: '#1e293b', textAlign: 'right' }}>{value}</span>
    </div>
  ));
  const vs = (o, unit) => o ? `${o.name} (${o.value}${unit || ''})` : '—';
  const dir = (o) => o ? `${o.zone} (${o.value})` : '—';

  const Seq = ({ seq }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
      {(seq || []).length === 0 ? <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No balls yet</span> :
        seq.map((t, i) => {
          const isW = t === 'W' || t === 'out';
          const isBoundary = t === '4' || t === '6';
          const isExtra = /wd|nb/.test(String(t));   // 'wd', 'nb', '6nb', 'wd(3)'
          // Retired hurt: not a wicket, so not red — but not an ordinary dot
          // either. Amber, like an extra, because it ends the innings without
          // a dismissal.
          const isRetired = t === 'RH';
          const bg = isRetired ? '#7c3aed' : isW ? '#dc2626' : isBoundary ? '#16a34a' : isExtra ? '#d97706' : '#f1f5f9';
          const col = (isRetired || isW || isBoundary || isExtra) ? 'white' : '#475569';
          return <span key={i} style={{ minWidth: 16, textAlign: 'center', padding: '2px 6px', borderRadius: 6, background: bg, color: col, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif' }}>{t}</span>;
        })}
    </div>
  );

  const Section = ({ title, color, children }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', color, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );

  // A single line inside an expanded points breakdown. Shows the formula (if any) and signed value.
  const BdLine = ({ label, formula, value }) => (value == null ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0 3px 14px', fontSize: '0.75rem', color: '#64748b', gap: 8 }}>
      <span>{label}{formula ? <span style={{ color: '#94a3b8', marginLeft: 6 }}>{formula}</span> : null}</span>
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{value >= 0 ? '+' : ''}{value}</span>
    </div>
  ));

  // The "X points" row — tap to expand the component breakdown beneath it.
  const PointsRow = ({ sectionKey, label, total, children }) => (
    <>
      <div onClick={() => toggleBd(sectionKey)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem', cursor: 'pointer' }}>
        <span style={{ color: '#64748b' }}>{label} <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700 }}>{openBd[sectionKey] ? '▲ hide' : 'ⓘ how?'}</span></span>
        <span style={{ fontWeight: 700, color: '#1e293b' }}>{total}</span>
      </div>
      {openBd[sectionKey] && (
        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '5px 0', margin: '4px 0 8px' }}>
          {children}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 14px 3px', fontSize: '0.78rem', fontWeight: 800, color: '#1E3A6E', borderTop: '1px solid #e2e8f0', marginTop: 3 }}>
            <span>Total</span><span>{total}</span>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 9999, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#1E3A6E', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
        <button onClick={onClose} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, padding: '7px 13px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', whiteSpace: 'nowrap' }}>← Back to scorecard</button>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data?.member?.name || 'Player'} <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>· this match</span>
        </div>
      </div>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '18px 16px 48px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Loading…</div>
          : loadErr ? <div style={{ textAlign: 'center', padding: 30, color: '#dc2626' }}>{loadErr}</div>
            : data ? (
              <>
                {data.show_points && (
                <div style={{ background: 'linear-gradient(135deg,#1E3A6E,#14532d)', color: 'white', borderRadius: 12, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>Total Points (day)</span>
                  <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#F5C518' }}>{data.points.total}</span>
                </div>
                )}

                {data.batting && (
                  <Section title="🏏 BATTING" color="#1E3A6E">
                    <Row label="Runs (balls)" value={`${data.batting.runs} (${data.batting.balls})`} />
                    <Row label="Strike rate" value={data.batting.strike_rate ?? '—'} />
                    <Row label="4s · 6s" value={`${data.batting.fours} · ${data.batting.sixes}`} />
                    <Row label="Most runs vs" value={vs(data.batting.most_runs_vs, ' r')} />
                    <Row label="Least runs vs" value={vs(data.batting.least_runs_vs, ' r')} />
                    <Row label="Most 4s vs" value={vs(data.batting.most_fours_vs)} />
                    <Row label="Most 6s vs" value={vs(data.batting.most_sixes_vs)} />
                    <Row label="Top run direction" value={dir(data.batting.top_direction)} />
                    {data.show_points && data.batting.points_breakdown && (
                      <PointsRow sectionKey="bat" label="Batting points" total={data.batting.points}>
                        <BdLine label={`Runs (${data.batting.runs})`} value={data.batting.points_breakdown.run_pts} />
                        {data.batting.points_breakdown.milestone_pts > 0 &&
                          <BdLine label={data.batting.runs >= 100 ? 'Century bonus' : 'Fifty bonus'} value={data.batting.points_breakdown.milestone_pts} />}
                        {data.batting.points_breakdown.boundary_pts > 0 &&
                          <BdLine label="Boundaries" formula={data.batting.points_breakdown.boundary_formula} value={data.batting.points_breakdown.boundary_pts} />}
                        <BdLine label="Strike-rate bonus" formula={data.batting.points_breakdown.sr_formula} value={data.batting.points_breakdown.sr_bonus} />
                      </PointsRow>
                    )}
                    <Seq seq={data.batting.sequence} />
                  </Section>
                )}

                {data.bowling && (
                  <Section title="🎳 BOWLING" color="#14532d">
                    <Row label="Overs · Runs · Wkts" value={`${data.bowling.overs} · ${data.bowling.runs} · ${data.bowling.wickets}`} />
                    <Row label="Maidens" value={data.bowling.maidens ?? 0} />
                    <Row label="Economy" value={data.bowling.economy ?? '—'} />
                    <Row label="Extras (wd+nb)" value={data.bowling.extras} />
                    <Row label="Most runs by" value={vs(data.bowling.most_runs_by, ' r')} />
                    <Row label="Least runs by" value={vs(data.bowling.least_runs_by, ' r')} />
                    <Row label="Most 4s by" value={vs(data.bowling.most_fours_by)} />
                    <Row label="Most 6s by" value={vs(data.bowling.most_sixes_by)} />
                    <Row label="Most-hit direction" value={dir(data.bowling.top_direction)} />
                    {data.show_points && data.bowling.points_breakdown && (
                      <PointsRow sectionKey="bowl" label="Bowling points" total={data.bowling.points}>
                        <BdLine label={`Wickets (${data.bowling.wickets}×20)`} value={data.bowling.points_breakdown.wicket_pts} />
                        {data.bowling.points_breakdown.maiden_pts > 0 &&
                          <BdLine label={`Maidens (${data.bowling.maidens}×10)`} value={data.bowling.points_breakdown.maiden_pts} />}
                        {data.bowling.points_breakdown.haul_pts > 0 &&
                          <BdLine label={data.bowling.wickets >= 5 ? '5-wicket haul' : '3-wicket haul'} value={data.bowling.points_breakdown.haul_pts} />}
                        <BdLine label="Economy bonus" formula={data.bowling.points_breakdown.econ_formula} value={data.bowling.points_breakdown.econ_bonus} />
                      </PointsRow>
                    )}
                    <Seq seq={data.bowling.sequence} />
                  </Section>
                )}

                <Section title="🧤 FIELDING" color="#b45309">
                  <Row label="Catches" value={data.fielding.catches} />
                  {data.fielding.caught_behind > 0 && <Row label="Caught behind (†)" value={data.fielding.caught_behind} />}
                  {data.fielding.stumpings > 0 && <Row label="Stumpings" value={data.fielding.stumpings} />}
                  {data.fielding.run_outs > 0 && <Row label="Run outs" value={data.fielding.run_outs} />}
                  <Row label="Misfields" value={data.fielding.misfields} />
                  <Row label="Drops" value={data.fielding.drops} />
                  <Row label="Most catches at" value={dir(data.fielding.top_position)} />
                  {data.show_points && data.fielding.points_breakdown && (
                    <PointsRow sectionKey="field" label="Fielding points" total={data.fielding.points}>
                      <BdLine label={`Dismissals (${data.fielding.points_breakdown.dismissals}×${data.fielding.points_breakdown.per_action})`} value={data.fielding.points_breakdown.dismissal_pts} />
                    </PointsRow>
                  )}
                </Section>
              </>
            ) : null}
      </div>
    </div>,
    document.body
  );
}

function UCCCommentaryTab({ innings, balls }) {
  // Innings toggle: default to innings 1.
  const [activeInn, setActiveInn] = useState(1);

  // Build an interleaved list of {type: 'ball'|'over_recap'|'milestone'} items
  // by walking balls in chronological order. We accumulate per-batter and
  // per-bowler running figures so each over-recap can show batters with
  // runs(balls), bowler with O-M-R-W after the over, and any milestones
  // achieved during the over (50 / 100 / 3W / 5W).
  // Result is then reversed so newest content shows on top.
  function buildRenderList(myBalls) {
    if (!myBalls || myBalls.length === 0) return [];

    const batter = new Map(); // id → {id, name, runs, balls, fours, sixes, out}
    const bowler = new Map(); // id → {id, name, legal, runs, wkts, maidens, _thisOverRuns}

    function bumpBatter(id, name, runs, addFaced) {
      if (!id) return;
      if (!batter.has(id)) batter.set(id, { id, name: name || '—', runs: 0, balls: 0, fours: 0, sixes: 0, out: false });
      const e = batter.get(id);
      if (addFaced) e.balls += 1;
      e.runs += runs;
      if (runs === 4) e.fours += 1;
      if (runs === 6) e.sixes += 1;
    }
    function bumpBowler(id, name, runs, isLegal, wktCredit) {
      if (!id) return;
      if (!bowler.has(id)) bowler.set(id, { id, name: name || '—', legal: 0, runs: 0, wkts: 0, maidens: 0, _thisOverRuns: 0 });
      const e = bowler.get(id);
      if (isLegal) e.legal += 1;
      e.runs += runs;
      e._thisOverRuns += runs;
      if (wktCredit) e.wkts += 1;
    }

    let teamRuns = 0, teamWkts = 0;
    let curOver = null;
    let overRuns = 0, overWkts = 0;
    let overFirstIdx = 0;
    let overMilestones = [];

    function snapshotBatters(strikerId) {
      const rows = [];
      const striker = batter.get(strikerId);
      if (striker && !striker.out) rows.push({ ...striker, isStriker: true });
      // Partner = most-recent not-out batter who isn't striker (Map preserves insertion order)
      const partners = Array.from(batter.values()).filter(b => !b.out && b.id !== strikerId);
      if (partners.length > 0) rows.push({ ...partners[partners.length - 1], isStriker: false });
      return rows;
    }

    const out = [];

    for (let i = 0; i < myBalls.length; i++) {
      const b = myBalls[i];
      const bTotal = (Number(b.runs_off_bat) || 0) + (Number(b.wides) || 0) + (Number(b.noballs) || 0) + (Number(b.byes) || 0) + (Number(b.legbyes) || 0);
      const overNo = Number(b.over_number);

      // Emit recap when over number changes
      if (curOver !== null && overNo !== curOver) {
        const prev = myBalls[i - 1];
        const bowlerObj = bowler.get(prev.bowler_id);
        const legalInOver = myBalls.slice(overFirstIdx, i).filter(x => x.is_legal && x.bowler_id === prev.bowler_id).length;
        if (bowlerObj && bowlerObj._thisOverRuns === 0 && legalInOver === 6) bowlerObj.maidens += 1;
        if (bowlerObj) bowlerObj._thisOverRuns = 0;

        out.push({
          type: 'over_recap',
          over_number: curOver,
          over_runs: overRuns,
          over_wkts: overWkts,
          team_runs: teamRuns,
          team_wkts: teamWkts,
          batters: snapshotBatters(prev.striker_id),
          bowler: bowlerObj ? {
            name: bowlerObj.name,
            overs: bowlerObj.legal / 6,
            maidens: bowlerObj.maidens,
            runs: bowlerObj.runs,
            wickets: bowlerObj.wkts,
          } : null,
          milestones: overMilestones.slice(),
        });
        overMilestones = [];
        overRuns = 0;
        overWkts = 0;
        overFirstIdx = i;
      }
      curOver = overNo;

      // Push the ball itself
      out.push({ type: 'ball', ball: b });

      // Update aggregates
      const batRuns = Number(b.runs_off_bat) || 0;
      bumpBatter(b.striker_id, b.striker_name, batRuns, !b.is_penalty && !(Number(b.wides) > 0)); // faced: all but wides

      const bowlerCreditTypes = ['bowled', 'caught', 'lbw', 'stumped', 'hit wicket', 'caught behind', 'caught and bowled'];
      const dismissType = String(b.dismissal_type || '').toLowerCase().replace(/_/g, ' ');
      const wktCredit = !!b.dismissal_type && bowlerCreditTypes.includes(dismissType);

      const bowlerCharged = batRuns + (Number(b.wides) || 0) + (Number(b.noballs) || 0);
      bumpBowler(b.bowler_id, b.bowler_name, bowlerCharged, !!b.is_legal, wktCredit);

      // Mark dismissed batter
      if (b.dismissal_type && b.dismissed_player_id) {
        const dId = b.dismissed_player_id;
        const dName = b.dismissed_name || (dId === b.striker_id ? b.striker_name : b.non_striker_name);
        if (!batter.has(dId)) batter.set(dId, { id: dId, name: dName || '—', runs: 0, balls: 0, fours: 0, sixes: 0, out: false });
        batter.get(dId).out = true;
        teamWkts += 1;
        overWkts += 1;

        if (wktCredit && b.bowler_id) {
          const bo = bowler.get(b.bowler_id);
          if (bo) {
            if (bo.wkts === 3) overMilestones.push({ type: '3w', who: bo.name });
            else if (bo.wkts === 5) overMilestones.push({ type: '5w', who: bo.name });
          }
        }
      }

      // Batter milestones — only after batRuns updated
      if (batRuns > 0 && b.striker_id) {
        const ba = batter.get(b.striker_id);
        if (ba) {
          const prevRuns = ba.runs - batRuns;
          if (prevRuns < 50 && ba.runs >= 50 && ba.runs < 100) overMilestones.push({ type: 'fifty', who: ba.name, runs: ba.runs });
          if (prevRuns < 100 && ba.runs >= 100)                overMilestones.push({ type: 'hundred', who: ba.name, runs: ba.runs });
        }
      }

      teamRuns += bTotal;
      overRuns += bTotal;
    }

    // Final recap for the last over
    if (curOver !== null && myBalls.length > 0) {
      const last = myBalls[myBalls.length - 1];
      const bowlerObj = bowler.get(last.bowler_id);
      const legalInOver = myBalls.slice(overFirstIdx).filter(x => x.is_legal && x.bowler_id === last.bowler_id).length;
      if (bowlerObj && bowlerObj._thisOverRuns === 0 && legalInOver === 6) bowlerObj.maidens += 1;
      out.push({
        type: 'over_recap',
        over_number: curOver,
        over_runs: overRuns,
        over_wkts: overWkts,
        team_runs: teamRuns,
        team_wkts: teamWkts,
        batters: snapshotBatters(last.striker_id),
        bowler: bowlerObj ? {
          name: bowlerObj.name,
          overs: bowlerObj.legal / 6,
          maidens: bowlerObj.maidens,
          runs: bowlerObj.runs,
          wickets: bowlerObj.wkts,
        } : null,
        milestones: overMilestones.slice(),
        is_final: true,
      });
    }

    return out;
  }

  const milestoneChip = (m, i) => {
    const styles = {
      fifty:   { bg: '#F5C518', fg: '#1E3A6E', label: '🏆 50' },
      hundred: { bg: '#F59E0B', fg: '#fff',    label: '💯 100' },
      '3w':    { bg: '#dc2626', fg: '#fff',    label: '🎯 3W' },
      '5w':    { bg: '#7c1d1d', fg: '#fff',    label: '🔥 5W' },
    };
    const s = styles[m.type];
    if (!s) return null;
    return (
      <span key={i} style={{
        background: s.bg, color: s.fg, padding: '3px 9px', borderRadius: 10,
        fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.04em',
        marginRight: 4, marginTop: 4, display: 'inline-block',
      }}>
        {s.label} · {m.who}{m.runs ? ` (${m.runs})` : ''}
      </span>
    );
  };

  function OverRecap({ rec }) {
    return (
      <div style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        borderLeft: '3px solid #1E3A6E',
        padding: '10px 14px 11px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#1E3A6E', letterSpacing: '0.1em' }}>
              END OF OVER {rec.over_number + 1}
            </span>
            <span style={{
              background: '#1E3A6E', color: '#F5C518',
              fontSize: '0.65rem', fontWeight: 800,
              padding: '2px 8px', borderRadius: 10,
              fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.02em',
            }}>
              {rec.over_runs} RUN{rec.over_runs === 1 ? '' : 'S'}
            </span>
            {rec.over_wkts > 0 && (
              <span style={{
                background: '#dc2626', color: 'white',
                fontSize: '0.65rem', fontWeight: 800,
                padding: '2px 8px', borderRadius: 10,
                fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.02em',
              }}>
                {rec.over_wkts} W
              </span>
            )}
          </div>
          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem', color: '#1E3A6E' }}>
            {rec.team_runs}<span style={{ color: '#F5C518', fontSize: '0.85rem' }}>/{rec.team_wkts}</span>
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 }}>🏏 BATTING</div>
            {rec.batters.length === 0 && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>—</div>}
            {rec.batters.map((ba, i) => (
              <div key={ba.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: i === rec.batters.length - 1 ? 0 : 4 }}>
                <span style={{
                  fontSize: '0.72rem', color: '#1E3A6E',
                  fontWeight: ba.isStriker ? 700 : 500,
                }}>
                  {ba.name}{ba.isStriker ? ' *' : ''}
                </span>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#1e293b' }}>
                  {ba.runs}
                  <span style={{ color: '#94a3b8', fontSize: '0.66rem', fontWeight: 500, marginLeft: 3 }}>({ba.balls})</span>
                </span>
              </div>
            ))}
          </div>
          <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 }}>🎳 BOWLING</div>
            {rec.bowler ? (
              <>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1E3A6E', lineHeight: 1.2, marginBottom: 5 }}>
                  {rec.bowler.name}
                </div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '0.78rem', lineHeight: 1.3 }}>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{rec.bowler.overs.toFixed(1)}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 500, marginLeft: 2 }}>O</span>
                  <span style={{ color: '#cbd5e1', margin: '0 5px' }}>·</span>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{rec.bowler.maidens}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 500, marginLeft: 2 }}>M</span>
                  <span style={{ color: '#cbd5e1', margin: '0 5px' }}>·</span>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{rec.bowler.runs}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 500, marginLeft: 2 }}>R</span>
                  <span style={{ color: '#cbd5e1', margin: '0 5px' }}>·</span>
                  <span style={{ fontWeight: 700, color: rec.bowler.wickets > 0 ? '#dc2626' : '#1e293b' }}>{rec.bowler.wickets}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 500, marginLeft: 2 }}>W</span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>—</div>
            )}
          </div>
        </div>
        {rec.milestones && rec.milestones.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {rec.milestones.map((m, i) => milestoneChip(m, i))}
          </div>
        )}
      </div>
    );
  }

  // Find the selected innings + its balls (chronological order from backend)
  const inn = (innings || []).find(i => i.innings_number === activeInn);
  const myBalls = inn ? balls.filter(b => b.innings_id === inn.id) : [];
  // Build the render list, then reverse so newest content is on top.
  const renderList = buildRenderList(myBalls).slice().reverse();

  // ── Voice commentary player ────────────────────────────────────
  // Uses Web Speech API (window.speechSynthesis). Plays all balls in
  // chronological order (oldest first), pausing between each.
  const [voiceState, setVoiceState] = React.useState('idle'); // 'idle' | 'playing' | 'paused'
  const voiceRef = React.useRef({ idx: 0, utterances: [], state: 'idle' });

  // Build chronological commentary lines for playback
  const voiceLines = React.useMemo(() => {
    if (!myBalls || myBalls.length === 0) return [];
    return myBalls.map(b => {
      if (b.commentary_text) return b.commentary_text;
      // Fallback for balls without AI commentary
      if (b.dismissal_type) return `${b.striker_name || 'Batter'} is out, ${b.dismissal_type.replace(/_/g, ' ')}, bowled by ${b.bowler_name || 'bowler'}.`;
      if (b.wides > 0) return `Wide ball from ${b.bowler_name || 'bowler'}.`;
      if (b.noballs > 0) return `No ball from ${b.bowler_name || 'bowler'}.`;
      if (b.byes > 0) return `${b.byes} bye${b.byes > 1 ? 's' : ''}.`;
      if (b.legbyes > 0) return `${b.legbyes} leg bye${b.legbyes > 1 ? 's' : ''}.`;
      if (b.runs_off_bat === 6) return `Six! ${b.striker_name || 'Batter'} off ${b.bowler_name || 'bowler'}.`;
      if (b.runs_off_bat === 4) return `Four! ${b.striker_name || 'Batter'} off ${b.bowler_name || 'bowler'}.`;
      if (b.runs_off_bat > 0) return `${b.runs_off_bat} run${b.runs_off_bat > 1 ? 's' : ''}. ${b.striker_name || 'Batter'} off ${b.bowler_name || 'bowler'}.`;
      return `Dot ball. ${b.bowler_name || 'Bowler'} to ${b.striker_name || 'batter'}.`;
    });
  }, [myBalls]);

  const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  function stopVoice() {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    voiceRef.current.state = 'idle';
    voiceRef.current.idx = 0;
    setVoiceState('idle');
  }

  function speakFrom(idx) {
    if (!speechSupported || idx >= voiceLines.length) {
      setVoiceState('idle');
      voiceRef.current.state = 'idle';
      return;
    }
    voiceRef.current.idx = idx;
    voiceRef.current.state = 'playing';

    const utter = new window.SpeechSynthesisUtterance(voiceLines[idx]);
    utter.lang = 'en-IN';
    utter.rate = 1.05;
    utter.pitch = 1.0;
    utter.onend = () => {
      if (voiceRef.current.state !== 'playing') return;
      // 800ms pause between balls
      setTimeout(() => {
        if (voiceRef.current.state === 'playing') {
          speakFrom(voiceRef.current.idx + 1);
        }
      }, 800);
    };
    utter.onerror = () => {
      if (voiceRef.current.state === 'playing') speakFrom(voiceRef.current.idx + 1);
    };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  }

  function playVoice() {
    if (!speechSupported || voiceLines.length === 0) return;
    voiceRef.current.state = 'playing';
    setVoiceState('playing');
    speakFrom(voiceRef.current.idx);
  }

  function pauseVoice() {
    if (!speechSupported) return;
    voiceRef.current.state = 'paused';
    setVoiceState('paused');
    window.speechSynthesis.cancel(); // pause = stop current, resume will re-speak from idx
  }

  function resumeVoice() {
    if (!speechSupported) return;
    voiceRef.current.state = 'playing';
    setVoiceState('playing');
    speakFrom(voiceRef.current.idx);
  }

  // Stop on tab change / unmount
  React.useEffect(() => {
    return () => stopVoice();
  }, [activeInn]);

  // Reset position when innings changes
  React.useEffect(() => {
    stopVoice();
  }, [activeInn]);

  const hasVoiceLines = speechSupported && voiceLines.length > 0;

  // Download all commentary as CSV
  function downloadCommentaryCSV() {
    if (!myBalls || myBalls.length === 0) return;
    const rows = [['Over', 'Ball', 'Label', 'Striker', 'Bowler', 'Runs', 'Wide', 'NB', 'Dismissal', 'Commentary']];
    myBalls.forEach(b => {
      rows.push([
        b.over_number,
        b.ball_in_over,
        b.display_label || `${b.over_number}.${b.ball_in_over}`,
        b.striker_name || '',
        b.bowler_name || '',
        b.runs_off_bat || 0,
        b.wides || 0,
        b.noballs || 0,
        b.dismissal_type || '',
        (b.commentary_text || '').replace(/,/g, ';').replace(/\r?\n/g, ' '),
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commentary_inn${activeInn}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xs)' }}>
      <InningsToggle innings={innings} value={activeInn} onChange={setActiveInn} />
      {/* Voice player bar */}
      {hasVoiceLines && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#1E3A6E', borderRadius: 10,
          padding: '8px 12px', marginBottom: 10,
        }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.06em', flex: 1 }}>
            🔊 LISTEN TO COMMENTARY
          </span>
          {voiceState === 'idle' && (
            <button onClick={playVoice} style={{ border: 'none', background: '#F5C518', color: '#1E3A6E', borderRadius: 6, padding: '5px 12px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif' }}>
              ▶ PLAY
            </button>
          )}
          {voiceState === 'playing' && (
            <button onClick={pauseVoice} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: 6, padding: '5px 12px', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif' }}>
              ⏸ PAUSE
            </button>
          )}
          {voiceState === 'paused' && (
            <button onClick={resumeVoice} style={{ border: 'none', background: '#F5C518', color: '#1E3A6E', borderRadius: 6, padding: '5px 12px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif' }}>
              ▶ RESUME
            </button>
          )}
          {voiceState !== 'idle' && (
            <button onClick={stopVoice} style={{ border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fca5a5', borderRadius: 6, padding: '5px 10px', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif' }}>
              ■ STOP
            </button>
          )}
          {voiceState !== 'idle' && (
            <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
              {voiceRef.current.idx + 1}/{voiceLines.length}
            </span>
          )}
          <button onClick={downloadCommentaryCSV} title="Download commentary as CSV" style={{ border: 'none', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '5px 8px', fontSize: '0.8rem', cursor: 'pointer' }}>
            ⬇
          </button>
        </div>
      )}
      {(!inn || myBalls.length === 0) ? (
        <div className="card mt-sm"><div className="card__body" style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
          No commentary available for this innings.
        </div></div>
      ) : (
        <div className="card mb-md" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#1E3A6E' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.08em' }}>
              💬 {inn.batting_team || `INNINGS ${inn.innings_number}`} — {inn.total_runs}/{inn.wickets} in {oversFromLegal(inn.legal_balls)}
            </span>
          </div>
          <div>
            {renderList.map((item, idx) => {
              if (item.type === 'over_recap') {
                return <OverRecap key={`rec-${idx}`} rec={item} />;
              }
              const b = item.ball;
              return (
                <div key={b.id} style={{
                  padding: '8px 14px',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  background: b.is_milestone_ball ? '#FFFDF0' : 'white',
                }}>
                  <div style={{
                    width: 50, flexShrink: 0,
                    fontSize: '0.7rem', color: '#64748b', fontFamily: 'monospace',
                    fontVariantNumeric: 'tabular-nums', paddingTop: 2,
                  }}>
                    {b.display_label || `${b.over_number}.${b.ball_in_over}`}
                    {/* Push 4 (Session 21 — 26 May 2026): per-ball IST timestamp. */}
                    {b.created_at && (
                      <div style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: 2, fontWeight: 500 }}>
                        {fmtShortTime(b.created_at)}
                      </div>
                    )}
                  </div>
                  <div style={{
                    width: 28, height: 28, flexShrink: 0, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: ballColor(b) === '#9CA3AF' ? '#f3f4f6' : ballColor(b),
                    color: ballColor(b) === '#9CA3AF' ? '#9CA3AF' : (ballColor(b) === '#F5C518' ? '#1E3A6E' : 'white'),
                    fontSize: '0.7rem', fontWeight: 800, border: '1px solid #d1d5db',
                  }}>
                    {classifyBallLabel(b)}
                  </div>
                  <div style={{ flex: 1, fontSize: '0.82rem', lineHeight: 1.45, color: '#1f2937' }}>
                    {b.is_milestone_ball && (
                      <span style={{
                        display: 'inline-block', marginRight: 6, padding: '1px 6px',
                        background: '#F5C518', color: '#1E3A6E', borderRadius: 8,
                        fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.04em',
                      }}>
                        🏆 {b.milestone_type === 'fifty' ? '50' :
                            b.milestone_type === 'hundred' ? '100' :
                            b.milestone_type === 'three_wkts' ? '3W' :
                            b.milestone_type === 'five_wkts' ? '5W' :
                            b.milestone_type === 'hat_trick' ? 'HAT-TRICK' : '—'}
                      </span>
                    )}
                    {b.commentary_text || `${b.bowler_name || 'Bowler'} to ${b.striker_name || 'batter'} — ${classifyBallLabel(b)}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ProfilePage ──────────────────────────────────────────
// ─── NotificationToggle ──────────────────────────────────
// A settings toggle to enable/disable Web Push notifications for this device.
function NotificationToggle() {
  const [status, setStatus] = useState({ supported: false, subscribed: false, permission: 'default' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) { setLoading(false); return; }
    getPushStatus().then(s => { setStatus(s); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    try {
      if (status.subscribed) {
        await unsubscribeFromPush();
        setStatus(s => ({ ...s, subscribed: false }));
        toast.success('Notifications turned off for this device');
      } else {
        await subscribeToPush();
        setStatus(s => ({ ...s, subscribed: true, permission: 'granted' }));
        toast.success('You\'ll now get notified of new notices!');
      }
    } catch (err) {
      toast.error(err.message || 'Could not update notification setting');
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    try {
      await api.post('/push/test');
      toast.success('Test notification sent');
    } catch (err) {
      toast.error('Failed to send test');
    }
  }

  if (loading) return null;

  const containerStyle = {
    background:'white', border:'1px solid var(--border, #e5e7eb)', borderRadius:12,
    padding:'14px 16px', marginBottom:'var(--space-md)',
  };

  if (!status.supported) {
    return (
      <div style={containerStyle}>
        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827', marginBottom:4 }}>🔔 Push Notifications</div>
        <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>
          Your browser doesn't support push notifications. Try Chrome on Android, or add this app to your home screen on iPhone (iOS 16.4+).
        </div>
      </div>
    );
  }

  if (status.permission === 'denied') {
    return (
      <div style={containerStyle}>
        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827', marginBottom:4 }}>🔔 Push Notifications</div>
        <div style={{ fontSize:'0.78rem', color:'#6b7280', lineHeight:1.5 }}>
          Notifications are <strong>blocked</strong> in your browser settings. To enable: tap the lock icon next to the URL → Notifications → Allow, then reload this page.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827', marginBottom:2 }}>🔔 Push Notifications</div>
          <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>
            {status.subscribed
              ? 'On — you\'ll get notified when new notices are posted'
              : 'Get notified on your phone whenever a new notice is posted'}
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
          style={{
            position:'relative', width:48, height:28, borderRadius:14, border:'none',
            background: status.subscribed ? 'var(--success, #16a34a)' : '#d1d5db',
            cursor: busy ? 'wait' : 'pointer', transition:'background 0.2s', flexShrink:0,
          }}>
          <span style={{
            position:'absolute', top:3, left: status.subscribed ? 23 : 3, width:22, height:22,
            borderRadius:'50%', background:'white', transition:'left 0.2s',
            boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>
      {status.subscribed && (
        <button onClick={sendTest}
          style={{
            width:'100%', marginTop:10, padding:'7px 0', borderRadius:8,
            border:'1px dashed #d1d5db', background:'white', color:'#6b7280',
            fontSize:'0.72rem', fontWeight:600, cursor:'pointer',
          }}>
          Send test notification
        </button>
      )}
    </div>
  );
}

// ─── ProfilePage ─────────────────────────────────────────
export function ProfilePage() {
  const { member: authMember, logout, refreshMember } = useAuth();
  const [member, setMember] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);

  async function uploadPhoto(file) {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(apiBase + '/upload/image', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + localStorage.getItem('ucc_token') },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      // Save immediately to profile
      await api.put('/members/' + member.id, { photo_url: data.url });
      const r = await api.get('/members/' + member.id);
      setMember(r.data.member);
      await refreshMember();
      toast.success('Photo updated ✅');
    } catch (err) {
      toast.error(err.message || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  }

  useEffect(() => {
    if (!authMember?.id) return;
    // Always fetch full profile from API — AuthContext only has basic fields
    api.get('/members/' + authMember.id)
      .then(r => {
        const m = r.data.member;
        setMember(m);
        setForm({
          mobile: m.mobile || '',
          dob: m.dob ? m.dob.split('T')[0] : '',
          anniversary: m.anniversary ? m.anniversary.split('T')[0] : '',
          emergency_name: m.emergency_name || '',
          emergency_mobile: m.emergency_mobile || '',
          emergency_relation: m.emergency_relation || '',
          cricheroes_name: m.cricheroes_name || '',
          spouse_name: m.spouse_name || '',
        });
      })
      .catch(() => setMember(authMember))
      .finally(() => setLoading(false));
  }, [authMember?.id]);

  async function save() {
    setSaving(true);
    try {
      await api.put('/members/' + member.id, { ...form, profile_complete: true });
      // Refresh full profile
      const r = await api.get('/members/' + member.id);
      setMember(r.data.member);
      await refreshMember();
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!member) return null;

  return (
    <div>
      <div style={{ background: "white", borderBottom: "1px solid var(--border)", padding: "16px", position: "relative", overflow: "hidden", textAlign: 'center' }}>
        <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto var(--space-md)' }}>
          <div className="avatar avatar-lg" style={{ fontSize: '2rem', border: '3px solid #F5C518', width: 80, height: 80 }}>
            {member.photo_url ? <img src={member.photo_url} alt={member.display_name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : member.display_name?.charAt(0)}
          </div>
          {/* Camera overlay button */}
          <label style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, background: '#F5C518', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.8rem', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }}>
            {photoUploading ? '⏳' : '📷'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadPhoto(e.target.files[0])} />
          </label>
        </div>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif' }}>{member.display_name}</h2>
        <p style={{ color: 'rgba(255,255,255,.6)' }}>{member.ucc_id} • {member.role?.toUpperCase()}</p>
        {member.jersey_no && <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '0.85rem' }}>Jersey #{member.jersey_no}</p>}
      </div>

      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        <div className="card mb-md">
          <div className="card__header">
            <span className="card__title">Profile Details</span>
            <button onClick={() => setEditing(!editing)} style={{ background: 'none', border: 'none', color: 'var(--navy)', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
              {editing ? 'Cancel' : 'Edit'}
            </button>
          </div>
          <div className="card__body">
            {[
              { label: 'Email', value: member.email },
              { label: 'UCC ID', value: member.ucc_id },
              { label: 'Joined', value: member.joining_month_year },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--ucc-gray-100)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--ucc-gray-500)' }}>{row.label}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{row.value || '—'}</span>
              </div>
            ))}
            {/* Static fields always shown */}
            {[
              { label: 'Mobile', value: member.mobile },
              { label: 'Date of Birth', value: member.dob ? new Date(member.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null },
              { label: 'Anniversary', value: member.anniversary ? new Date(member.anniversary).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null },
              { label: 'Emergency Contact', value: member.emergency_name ? `${member.emergency_name}${member.emergency_relation ? ' (' + member.emergency_relation + ')' : ''} · ${member.emergency_mobile}` : null },
              { label: 'CricHeroes Name', value: member.cricheroes_name },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--ucc-gray-100)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--ucc-gray-500)' }}>{row.label}</span>
                <span style={{ fontWeight: 600, fontSize: '0.88rem', textAlign: 'right', maxWidth: '65%' }}>{row.value || '—'}</span>
              </div>
            ))}
            {editing && (
              <>
                <div style={{ borderTop: '2px solid var(--ucc-gray-200)', marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--ucc-gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-sm)' }}>Update Details</div>
                  {[
                    { label: 'Mobile', key: 'mobile', type: 'tel', mode: 'tel' },
                    { label: 'Date of Birth', key: 'dob', type: 'date', mode: null },
                    { label: 'Anniversary (leave blank if N/A)', key: 'anniversary', type: 'date', mode: null },
                    { label: 'Emergency Contact Name', key: 'emergency_name', type: 'text', mode: null },
                    { label: 'Emergency Mobile', key: 'emergency_mobile', type: 'tel', mode: 'tel' },
                    { label: 'Spouse / Partner Name (leave blank if N/A)', key: 'spouse_name', type: 'text', mode: null },
                  ].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input type={f.type} className="form-control" value={form[f.key]} onChange={e => setForm(ff => ({ ...ff, [f.key]: e.target.value }))} inputMode={f.mode || undefined} />
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">Emergency Relation</label>
                    <select className="form-control" value={form.emergency_relation} onChange={e => setForm(f => ({ ...f, emergency_relation: e.target.value }))}>
                      <option value="">Select…</option>
                      {['Spouse','Father','Mother','Brother','Sister','Son','Daughter','Friend','Other'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary btn-block" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        <NotificationToggle />

        <Link to={`/stats/${member.id}`} className="btn btn-outline btn-block mb-md">📊 View My Stats</Link>
        <Link to="/attendance" className="btn btn-outline btn-block mb-md">📅 My Attendance</Link>
        <button onClick={logout} className="btn btn-danger btn-block" style={{ marginTop: 'var(--space-md)' }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── SeriesPage ───────────────────────────────────────────
export function SeriesPage() {
  const { member: me } = useAuth();
  const [seriesList, setSeriesList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  // A captain picks his own XI for a fixture he leads. Squads were settled
  // before the series; here he chooses from his own squad, and only those who
  // said YES for this date.
  const [capFixture, setCapFixture] = useState(null);
  const [capData, setCapData]       = useState(null);
  const [capPicks, setCapPicks]     = useState(new Set());
  const [capSaving, setCapSaving]   = useState(false);

  async function openCaptainPicker(f) {
    setCapFixture(f);
    setCapData(null);
    setCapPicks(new Set());
    try {
      const r = await api.get(`/series/${selected.id}/fixtures/${f.id}/pickable`);
      const mine = (row) => row.captain_id === me?.id;
      setCapData({
        yes: (r.data.pickable   || []).filter(mine),
        no:  (r.data.unavailable || []).filter(mine),
        // Squad members who have not played a match of this series yet.
        notPlayed: (r.data.not_played_yet || []).filter(mine),
      });
      setCapPicks(new Set((r.data.selected || []).filter(mine).map(x => x.member_id)));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not load your squad');
    }
  }

  async function saveCaptainXI() {
    if (!capFixture) return;
    setCapSaving(true);
    try {
      const r = await api.put(`/series/${selected.id}/fixtures/${capFixture.id}/squad`, {
        picks: [...capPicks].map(member_id => ({ member_id, captain_id: me.id })),
      });
      toast.success(r.data.both_sides_in
        ? `XI saved — both sides are in`
        : `XI saved — ${r.data.playing} players`);
      setCapFixture(null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save your XI');
    } finally { setCapSaving(false); }
  }

  useEffect(() => {
    api.get('/series').then(res => {
      setSeriesList(res.data.series || []);
      if (res.data.series?.length > 0) setSelected(res.data.series[0]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selected) api.get(`/series/${selected.id}`).then(res => setDetail(res.data));
  }, [selected]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background: "white", borderBottom: "1px solid var(--border)", padding: "16px", position: "relative", overflow: "hidden" }}>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'Rajdhani,sans-serif' }}>🏅 Series 2026</h2>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '12px', background: 'white', borderBottom: '1px solid var(--ucc-gray-200)', overflowX: 'auto' }}>
        {seriesList.map(s => (
          <button key={s.id} onClick={() => setSelected(s)}
            style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '2px solid', whiteSpace: 'nowrap', cursor: 'pointer',
              borderColor: selected?.id === s.id ? '#1E3A6E' : 'var(--ucc-gray-200)',
              background: selected?.id === s.id ? '#1E3A6E' : 'white',
              color: selected?.id === s.id ? 'white' : 'var(--ucc-gray-700)',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem' }}>
            {s.name}
          </button>
        ))}
      </div>
      {detail && (
        <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
          {/* Series info */}
          <div className="card mb-md">
            <div className="card__body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { l: 'Format', v: detail.series?.format?.charAt(0).toUpperCase() + detail.series?.format?.slice(1) },
                  { l: 'Status', v: detail.series?.status?.toUpperCase() },
                  { l: 'Innings', v: detail.series?.innings_per_match },
                  { l: 'Overs/Inn', v: detail.series?.overs_per_innings },
                ].map(r => (
                  <div key={r.l} style={{ padding: 8, background: 'var(--ucc-gray-100)', borderRadius: 8 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ucc-gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{r.l}</div>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>{r.v || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Captain: pick your XI ── */}
          {(detail.fixtures || []).some(f => f.capt1_id === me?.id || f.capt2_id === me?.id) && (
            <div className="card mb-md">
              <div style={{ padding: '10px 14px', background: '#1E3A6E', borderRadius: '10px 10px 0 0' }}>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#F5C518', letterSpacing: '0.08em' }}>
                  🧢 YOUR XI TO PICK
                </span>
              </div>
              <div style={{ padding: 12 }}>
                {!capFixture ? (
                  (detail.fixtures || [])
                    .filter(f => f.capt1_id === me?.id || f.capt2_id === me?.id)
                    .map((f, i) => (
                      <div key={f.id} onClick={() => openCaptainPicker(f)}
                        style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          padding:'12px 14px', marginBottom:8, borderRadius:10, cursor:'pointer',
                          border:'1px solid var(--ucc-gray-200)', background:'white' }}>
                        <div>
                          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:'#1E3A6E' }}>
                            Match {i + 1} — {f.capt1_id === me?.id ? (f.capt1_team || 'your side') : (f.capt2_team || 'your side')}
                          </div>
                          <div style={{ fontSize:'0.72rem', color:'var(--ucc-gray-500)' }}>
                            {f.match_date ? new Date(f.match_date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'}) : 'Date TBD'}
                            {f.venue ? ` · ${f.venue}` : ''}
                          </div>
                        </div>
                        <span style={{ color:'var(--ucc-gray-400)' }}>→</span>
                      </div>
                    ))
                ) : (<>
                  <button onClick={() => setCapFixture(null)}
                    style={{ background:'none', border:'none', color:'#1E3A6E', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', cursor:'pointer', padding:0, marginBottom:10 }}>
                    ← Back
                  </button>

                  {!capData ? (
                    <div style={{ padding:16, textAlign:'center', color:'var(--ucc-gray-500)' }}>Loading your squad…</div>
                  ) : (<>
                    <div style={{ fontSize:'0.72rem', color:'var(--ucc-gray-500)', marginBottom:8 }}>
                      Tap to select. Only your squad members who said YES for this date can play — the rest are rested.
                    </div>

                    {capData.notPlayed && capData.notPlayed.length > 0 && (() => {
                      const left = Math.max(...capData.notPlayed.map(m => m.fixtures_left || 0));
                      const last = left <= 1;
                      return (
                        <div style={{ padding:10, background: last ? '#FEE2E2' : '#FEF3C7', borderRadius:8, fontSize:'0.78rem',
                          color: last ? '#B91C1C' : '#92400e', marginBottom:10, lineHeight:1.5 }}>
                          <b>{last ? 'Last match — ' : ''}Every squad member must play at least one match.</b> Not played yet:{' '}
                          {capData.notPlayed.map((m, i) => (
                            <span key={m.member_id}>{i ? ', ' : ''}<b>{m.display_name}</b>{m.yes_today ? '' : ' (not available today)'}</span>
                          ))}
                          {!last && <> · {left} match{left === 1 ? '' : 'es'} left including this one.</>}
                        </div>
                      );
                    })()}

                    {capData.yes.length === 0 && (
                      <div style={{ padding:12, background:'#fef3c7', borderRadius:8, fontSize:'0.8rem', color:'#92400e', marginBottom:10 }}>
                        Nobody in your squad has said YES for this date yet.
                      </div>
                    )}

                    {capData.yes.map(m => {
                      const on = capPicks.has(m.member_id);
                      return (
                        <div key={m.member_id}
                          onClick={() => setCapPicks(prev => {
                            const next = new Set(prev);
                            if (next.has(m.member_id)) next.delete(m.member_id); else next.add(m.member_id);
                            return next;
                          })}
                          style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', marginBottom:6,
                            borderRadius:10, cursor:'pointer', border:'1px solid',
                            borderColor: on ? '#bbf7d0' : 'var(--ucc-gray-200)', background: on ? '#f0fdf4' : 'white' }}>
                          <div style={{ width:22, height:22, borderRadius:'50%', flexShrink:0,
                            background: on ? '#16a34a' : '#e5e7eb', color:'white', fontWeight:900, fontSize:'0.7rem',
                            display:'flex', alignItems:'center', justifyContent:'center' }}>{on ? '✓' : ''}</div>
                          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700 }}>{m.display_name}</div>
                          {capData.notPlayed?.some(x => x.member_id === m.member_id) && (
                            <span style={{ marginLeft:'auto', fontSize:'0.65rem', fontWeight:800, color:'#B45309', background:'#FEF3C7', padding:'2px 7px', borderRadius:8 }}>NOT PLAYED YET</span>
                          )}
                        </div>
                      );
                    })}

                    {capData.no.length > 0 && (
                      <div style={{ marginTop:10 }}>
                        <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#991b1b', marginBottom:4 }}>
                          NOT AVAILABLE FOR THIS DATE ({capData.no.length})
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                          {capData.no.map(m => (
                            <span key={m.member_id} style={{ fontSize:'0.72rem', background:'#fef2f2', color:'#991b1b', border:'1px solid #fecaca', padding:'3px 8px', borderRadius:20, fontWeight:600 }}>
                              {m.display_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <button onClick={saveCaptainXI} disabled={capSaving || capPicks.size === 0}
                      className="btn btn-primary btn-block" style={{ marginTop:12 }}>
                      {capSaving ? 'Saving…' : `Save my XI (${capPicks.size})`}
                    </button>
                  </>)}
                </>)}
              </div>
            </div>
          )}

          {/* Standings */}
          {detail.standings?.length > 0 && (
            <div className="card mb-md">
              <div style={{ padding: '10px 14px', background: '#1E3A6E', borderRadius: '10px 10px 0 0' }}>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#F5C518', letterSpacing: '0.08em' }}>🏆 STANDINGS</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Team', 'P', 'W', 'L'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Team' ? 'left' : 'center', fontWeight: 700, fontSize: '0.72rem', color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.standings.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i === 0 ? '#fffbeb' : 'white' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1E3A6E' }}>
                        {i === 0 && <span style={{ marginRight: 6 }}>🥇</span>}
                        {i === 1 && <span style={{ marginRight: 6 }}>🥈</span>}
                        {s.team}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#64748b' }}>{s.played}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{s.won}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#dc2626' }}>{s.lost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Best performances across this series — updates as fixtures are played */}
          {selected?.id && (
            <Link to={`/best-performances?series=${selected.id}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                       padding: '12px 14px', margin: '0 0 14px', borderRadius: 12, textDecoration: 'none',
                       background: 'linear-gradient(90deg,#1E3A6E,#2d4f8f)', color: 'white' }}>
              <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, letterSpacing: '0.04em' }}>
                🏅 BEST PERFORMANCES — batsman, bowler, fielder, keeper, man of the series
              </span>
              <span style={{ color: '#F5C518', fontWeight: 800 }}>›</span>
            </Link>
          )}

          {/* Fixtures */}
          {detail.fixtures?.length > 0 && (
            <div className="card">
              <div style={{ padding: '10px 14px', background: '#1E3A6E', borderRadius: '10px 10px 0 0' }}>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#F5C518', letterSpacing: '0.08em' }}>📅 MATCHES</span>
              </div>
              {detail.fixtures.map((f, i) => {
                const completed = !!f.winner_team;
                const cancelled = f.is_cancelled;
                // Winner by CAPTAIN. winner_team is CricHeroes' own text
                // ('LeafBerry Ads UPL 2026 (Takkar)') and never equals the
                // club's team name, so comparing names crowned nobody. The
                // name match stays only as a fallback for rows with no
                // captaincy winner recorded.
                const aWon = f.winner_id ? f.winner_id === f.capt1_id : f.winner_team === f.capt1_team;
                const bWon = f.winner_id ? f.winner_id === f.capt2_id : f.winner_team === f.capt2_team;
                const winnerName = aWon ? f.capt1_team : bWon ? f.capt2_team : null;
                // Captain slots are in fixture-creation order, not batting
                // order. When captain 2's side batted first, his scores are
                // innings 1 and 3 — swap so each column shows its own side.
                const sw = !!f.capt2_batted_first;
                const fx = sw ? {
                  inn1_runs: f.inn2_runs, inn1_wkts: f.inn2_wkts, inn1_overs: f.inn2_overs,
                  inn3_runs: f.inn4_runs, inn3_wkts: f.inn4_wkts, inn3_overs: f.inn4_overs,
                  inn2_runs: f.inn1_runs, inn2_wkts: f.inn1_wkts, inn2_overs: f.inn1_overs,
                  inn4_runs: f.inn3_runs, inn4_wkts: f.inn3_wkts, inn4_overs: f.inn3_overs,
                  team_a_score: f.team_b_score, team_b_score: f.team_a_score,
                } : f;
                return (
                  <div key={f.id} style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafbfc' }}>

                    {/* Date row */}
                    <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {f.match_date ? new Date(f.match_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD'}
                      {cancelled && <span style={{ marginLeft: 8, background: '#fee2e2', color: '#dc2626', padding: '1px 6px', borderRadius: 4 }}>CANCELLED</span>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      {/* Captain 1's side — scores swapped above if he batted second */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.05rem', color: aWon ? '#16a34a' : '#1E3A6E', lineHeight: 1.1 }}>
                          {aWon && '🏆 '}{f.capt_a_name || '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 6 }}>{f.capt1_team}</div>
                        {fx.inn1_runs != null
                          ? <>
                              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.2rem', color: '#1e293b', lineHeight: 1.2 }}>
                                {fx.inn1_runs}/{fx.inn1_wkts}
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginLeft: 4 }}>({fx.inn1_overs ? parseFloat(fx.inn1_overs).toFixed(1) : '—'} ov)</span>
                              </div>
                              {fx.inn3_runs != null && (
                                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#334155', lineHeight: 1.2, marginTop: 2 }}>
                                  {fx.inn3_runs}/{fx.inn3_wkts}
                                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', marginLeft: 4 }}>({fx.inn3_overs ? parseFloat(fx.inn3_overs).toFixed(1) : '—'} ov)</span>
                                </div>
                              )}
                            </>
                          : fx.team_a_score
                            ? <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.2rem', color: '#1e293b' }}>{fx.team_a_score}</div>
                            : <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>—</div>}
                      </div>

                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, color: '#e2e8f0', fontSize: '1rem', paddingTop: 4 }}>VS</div>

                      {/* Captain 2's side */}
                      <div style={{ flex: 1, textAlign: 'right' }}>
                        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.05rem', color: bWon ? '#16a34a' : '#1E3A6E', lineHeight: 1.1 }}>
                          {f.capt_b_name || '—'}{bWon && ' 🏆'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 6 }}>{f.capt2_team}</div>
                        {fx.inn2_runs != null
                          ? <>
                              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.2rem', color: '#1e293b', lineHeight: 1.2 }}>
                                {fx.inn2_runs}/{fx.inn2_wkts}
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginLeft: 4 }}>({fx.inn2_overs ? parseFloat(fx.inn2_overs).toFixed(1) : '—'} ov)</span>
                              </div>
                              {fx.inn4_runs != null && (
                                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#334155', lineHeight: 1.2, marginTop: 2 }}>
                                  {fx.inn4_runs}/{fx.inn4_wkts}
                                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', marginLeft: 4 }}>({fx.inn4_overs ? parseFloat(fx.inn4_overs).toFixed(1) : '—'} ov)</span>
                                </div>
                              )}
                            </>
                          : fx.team_b_score
                            ? <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1.2rem', color: '#1e293b' }}>{fx.team_b_score}</div>
                            : <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>—</div>}
                      </div>
                    </div>

                    {/* Result bar */}
                    {completed && (
                      <div style={{ padding: '5px 10px', background: '#f0fdf4', borderRadius: 6, fontSize: '0.78rem', color: '#15803d', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          {winnerName || f.winner_team} won
                          {f.margin ? ` by ${f.margin} ${f.margin_type}` : ''}
                        </span>
                        {f.motm_name && <span style={{ color: '#d97706' }}>⭐ {f.motm_name}</span>}
                      </div>
                    )}
                    {!completed && !cancelled && (
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>Result pending</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {detail.fixtures?.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No matches scheduled yet</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── UCCSummaryTab ─────────────────────────────────────────
// Shown for imported (past) matches. Stacked cards:
//   • Result banner with score line
//   • Both innings scores
//   • Man of the Match
//   • Top batter / bowler / fielder
//   • Best partnership
function UCCSummaryTab({ match, innings, summary, captaincy, matchSummary }) {
  if (!summary) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <div className="card"><div className="card__body" style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
          No summary available for this match.
        </div></div>
      </div>
    );
  }

  const innings1 = (innings || []).find(i => i.innings_number === 1);
  const innings2 = (innings || []).find(i => i.innings_number === 2);

  function fmtOvers(balls, max) {
    const ov  = Math.floor(balls / 6);
    const rem = balls % 6;
    return `${ov}.${rem}${max ? ` / ${max}` : ''}`;
  }
  function isWinningTeam(teamName) {
    return summary.winner_team && teamName && summary.winner_team.toUpperCase() === teamName.toUpperCase();
  }
  function captainFor(teamName) {
    if (!teamName || !captaincy) return null;
    const t = teamName.toUpperCase();
    if (captaincy.capt1_team && captaincy.capt1_team.toUpperCase() === t) return captaincy.capt1_name || null;
    if (captaincy.capt2_team && captaincy.capt2_team.toUpperCase() === t) return captaincy.capt2_name || null;
    return null;
  }

  // UCC role colors (matching squad-setup convention):
  //   batter = orange, bowler = purple, fielder = blue, keeper = green
  // Each performer card uses a 5px accent strip on the left + same hue for
  // the big number, with a darker shade for the section label.
  function PerformerCard({ icon, label, name, sub, primary, accent, labelColor }) {
    return (
      <div style={{
        background: 'white', borderRadius: 12, marginBottom: 8,
        display: 'flex', overflow: 'hidden', border: '1px solid #f1f5f9',
      }}>
        <div style={{ width: 5, background: accent, flexShrink: 0 }} />
        <div style={{ padding: '12px 14px', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: '0.85rem' }}>{icon}</span>
            <span style={{ fontSize: '0.62rem', fontWeight: 800, color: labelColor, letterSpacing: '0.1em' }}>
              {label}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1E3A6E', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {name}
              </div>
              {sub && (
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>{sub}</div>
              )}
            </div>
            <div style={{
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 800,
              fontSize: '1.35rem', color: accent, flexShrink: 0, lineHeight: 1,
            }}>
              {primary}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MOTM "performance line" — show whatever the MOM actually did
  // (batting if they batted, bowling if they bowled, fielding if any),
  // using the MOM's own figures from summary.motm.
  let motmStats = null;
  if (summary.motm && summary.motm.winner1_name) {
    const chips = [];
    const mb = summary.motm.batting, mw = summary.motm.bowling, mf = summary.motm.fielding;
    if (mb) {
      chips.push({ bg: '#ffedd5', fg: '#9a3412', text: `🏏 ${mb.runs} (${mb.balls})` });
      if (mb.sr != null) chips.push({ bg: '#f1f5f9', fg: '#475569', text: `SR ${Number(mb.sr).toFixed(1)}` });
    }
    if (mw) {
      chips.push({ bg: '#ede9fe', fg: '#5b21b6',
        text: `🎳 ${mw.wickets}/${mw.runs}${mw.overs != null ? ` (${mw.overs})` : ''}` });
    }
    if (mf) {
      const bits = [];
      // caught_behind is already counted inside catches — show c† instead of ct for keeper catches
      const outfieldCatches = Math.max(0, (mf.catches || 0) - (mf.caught_behind || 0));
      if (outfieldCatches)   bits.push(`${outfieldCatches} ct`);
      if (mf.caught_behind)  bits.push(`${mf.caught_behind} c†`);
      if (mf.stumpings)      bits.push(`${mf.stumpings} st`);
      if (mf.run_outs)       bits.push(`${mf.run_outs} ro`);
      if (bits.length) chips.push({ bg: '#dcfce7', fg: '#166534', text: `🧤 ${bits.join(' · ')}` });
    }
    // Fallback for older API responses that don't include MOM's own figures.
    if (chips.length === 0) {
      if (summary.top_batter && summary.top_batter.name === summary.motm.winner1_name) {
        chips.push({ bg: '#ffedd5', fg: '#9a3412', text: `🏏 ${summary.top_batter.runs} (${summary.top_batter.balls})` });
      }
      if (summary.top_bowler && summary.top_bowler.name === summary.motm.winner1_name) {
        chips.push({ bg: '#ede9fe', fg: '#5b21b6', text: `🎳 ${summary.top_bowler.wickets}/${summary.top_bowler.runs}` });
      }
    }
    motmStats = chips;
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--space-sm)' }}>
      {/* HERO RESULT — integrated scoreboard */}
      {summary.result_line && (
        <div style={{
          background: '#1E3A6E', color: 'white', borderRadius: 14,
          padding: '18px 16px', marginBottom: 12, position: 'relative', overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(30,58,110,0.15)',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, fontSize: '6rem', opacity: 0.06, lineHeight: 1 }}>🏆</div>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#F5C518', letterSpacing: '0.12em', marginBottom: 4 }}>
            FINAL RESULT
          </div>
          <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.01em', marginBottom: 14, lineHeight: 1.15 }}>
            {summary.result_line}
          </div>
          {(innings1 || innings2) && (
            <div style={{ borderTop: '1px solid rgba(245,197,24,0.25)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {innings1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{
                      fontSize: '0.82rem', fontWeight: 600,
                      color: isWinningTeam(innings1.batting_team) ? '#F5C518' : '#cbd5e1',
                    }}>
                      {innings1.batting_team}{isWinningTeam(innings1.batting_team) ? ' ✓' : ''}
                    </span>
                    {captainFor(innings1.batting_team) && (
                      <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>
                        c. {captainFor(innings1.batting_team)}
                      </span>
                    )}
                  </div>
                  <span style={{ fontFamily: 'Rajdhani,sans-serif', whiteSpace: 'nowrap', marginLeft: 10 }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 800 }}>{innings1.total_runs}</span>
                    <span style={{ color: '#F5C518', fontSize: '1rem', fontWeight: 700 }}>/{innings1.wickets}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: 6 }}>
                      {fmtOvers(innings1.legal_balls)} ov
                    </span>
                  </span>
                </div>
              )}
              {innings2 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{
                      fontSize: '0.82rem', fontWeight: 600,
                      color: isWinningTeam(innings2.batting_team) ? '#F5C518' : '#cbd5e1',
                    }}>
                      {innings2.batting_team}{isWinningTeam(innings2.batting_team) ? ' ✓' : ''}
                    </span>
                    {captainFor(innings2.batting_team) && (
                      <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>
                        c. {captainFor(innings2.batting_team)}
                      </span>
                    )}
                  </div>
                  <span style={{ fontFamily: 'Rajdhani,sans-serif', whiteSpace: 'nowrap', marginLeft: 10 }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 800 }}>{innings2.total_runs}</span>
                    <span style={{ color: '#F5C518', fontSize: '1rem', fontWeight: 700 }}>/{innings2.wickets}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: 6 }}>
                      {fmtOvers(innings2.legal_balls)} ov
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* MATCH SUMMARY COMMENTARY */}
      {matchSummary && (
        <div style={{
          background: 'white', borderRadius: 14, padding: '14px 16px', marginBottom: 12,
          border: '1px solid #e2e8f0', borderLeft: '4px solid #F5C518',
        }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 8, textTransform: 'uppercase' }}>
            🎙️ Match Report
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.65, color: '#334155', fontStyle: 'italic' }}>
            {matchSummary}
          </div>
        </div>
      )}

      {/* MAN OF THE MATCH */}
      {summary.motm && summary.motm.winner1_name && (
        <div style={{
          background: 'white', borderRadius: 14, padding: 16, marginBottom: 12,
          border: '1px solid #f1e8d4', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 4,
            background: 'linear-gradient(90deg,#f59e0b,#F5C518,#f59e0b)',
          }} />
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.12em', marginBottom: 6, textAlign: 'center' }}>
            🎖️ MAN OF THE MATCH
          </div>
          <div style={{
            fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.4rem',
            color: '#1E3A6E', textAlign: 'center', margin: '2px 0 10px',
          }}>
            {summary.motm.winner1_name}
            {summary.motm.is_joint && summary.motm.winner2_name && <span> & {summary.motm.winner2_name}</span>}
            {summary.motm.is_guest && (
              <span style={{ fontSize: '0.6rem', color: '#92400e', marginLeft: 6, verticalAlign: 'middle' }}>(GUEST)</span>
            )}
          </div>
          {motmStats && motmStats.length > 0 && (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
              {motmStats.map((c, i) => (
                <span key={i} style={{
                  background: c.bg, color: c.fg, padding: '4px 10px',
                  borderRadius: 12, fontSize: '0.66rem', fontWeight: 700,
                }}>
                  {c.text}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TOP BATTER — ORANGE */}
      {summary.top_batter && summary.top_batter.runs > 0 && (
        <PerformerCard
          icon="🏏"
          label="TOP BATTER"
          name={summary.top_batter.name}
          sub={[
            `${summary.top_batter.fours}×4`,
            `${summary.top_batter.sixes}×6`,
            summary.top_batter.sr != null ? `SR ${summary.top_batter.sr.toFixed(1)}` : null,
          ].filter(Boolean).join(' · ')}
          primary={<>
            {summary.top_batter.runs}
            <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600 }}> ({summary.top_batter.balls})</span>
          </>}
          accent="#ea580c"
          labelColor="#9a3412"
        />
      )}

      {/* TOP BOWLER — PURPLE */}
      {summary.top_bowler && summary.top_bowler.wickets > 0 && (
        <PerformerCard
          icon="🎳"
          label="TOP BOWLER"
          name={summary.top_bowler.name}
          sub={[
            summary.top_bowler.overs != null ? `${Number(summary.top_bowler.overs).toFixed(1)} ov` : null,
            summary.top_bowler.economy != null ? `Econ ${Number(summary.top_bowler.economy).toFixed(2)}` : null,
          ].filter(Boolean).join(' · ')}
          primary={`${summary.top_bowler.wickets}/${summary.top_bowler.runs}`}
          accent="#7c3aed"
          labelColor="#5b21b6"
        />
      )}

      {/* TOP FIELDER — BLUE (catches + run-outs, excluding keeper) */}
      {summary.top_fielder && summary.top_fielder.total > 0 && (
        <PerformerCard
          icon="🧤"
          label="TOP FIELDER"
          name={joinNames(summary.top_fielder.names || [summary.top_fielder.name])}
          sub={summary.top_fielder.joint
            ? `${summary.top_fielder.total} each`
            : [
                summary.top_fielder.catches  > 0 ? `${summary.top_fielder.catches} catch${summary.top_fielder.catches === 1 ? '' : 'es'}` : null,
                summary.top_fielder.run_outs > 0 ? `${summary.top_fielder.run_outs} run-out${summary.top_fielder.run_outs === 1 ? '' : 's'}` : null,
              ].filter(Boolean).join(' · ')}
          primary={summary.top_fielder.total}
          accent="#2563eb"
          labelColor="#1d4ed8"
        />
      )}

      {/* TOP KEEPER — GREEN (caught_behind + stumpings) */}
      {summary.top_keeper && summary.top_keeper.total > 0 && (
        <PerformerCard
          icon="🥅"
          label="TOP KEEPER"
          name={joinNames(summary.top_keeper.names || [summary.top_keeper.name])}
          sub={summary.top_keeper.joint
            ? `${summary.top_keeper.total} each`
            : [
                summary.top_keeper.caught_behind > 0 ? `${summary.top_keeper.caught_behind} c†` : null,
                summary.top_keeper.stumpings     > 0 ? `${summary.top_keeper.stumpings} stumping${summary.top_keeper.stumpings === 1 ? '' : 's'}` : null,
              ].filter(Boolean).join(' · ')}
          primary={summary.top_keeper.total}
          accent="#16a34a"
          labelColor="#15803d"
        />
      )}

      {/* BEST PARTNERSHIP — TEAL (not a role) */}
      {summary.best_partnership && summary.best_partnership.runs > 0 && (
        <div style={{
          background: 'white', borderRadius: 12, marginBottom: 8,
          display: 'flex', overflow: 'hidden', border: '1px solid #f1f5f9',
        }}>
          <div style={{ width: 5, background: '#0891b2', flexShrink: 0 }} />
          <div style={{ padding: '12px 14px', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: '0.85rem' }}>🤝</span>
              <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#0e7490', letterSpacing: '0.1em' }}>
                BEST PARTNERSHIP
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E3A6E', lineHeight: 1.35 }}>
                  {summary.best_partnership.player1_name}
                  <span style={{ color: '#64748b', fontWeight: 500 }}> ({summary.best_partnership.p1_runs || 0})</span>
                  <br />
                  <span style={{ color: '#94a3b8', fontWeight: 500 }}>&</span> {summary.best_partnership.player2_name}
                  <span style={{ color: '#64748b', fontWeight: 500 }}> ({summary.best_partnership.p2_runs || 0})</span>
                </div>
                {summary.best_partnership.wicket_number != null && (
                  <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
                    {ordinalWicket(summary.best_partnership.wicket_number)} wicket · Innings {summary.best_partnership.innings}
                  </div>
                )}
              </div>
              <div style={{
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 800,
                fontSize: '1.35rem', color: '#0891b2', flexShrink: 0, lineHeight: 1,
              }}>
                {summary.best_partnership.runs}
                <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600 }}> ({summary.best_partnership.balls})</span>
              </div>
            </div>
            <div style={{ fontSize: '0.62rem', color: '#94a3b8', fontStyle: 'italic', marginTop: 6 }}>
              * includes extras during the partnership
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 'A', 'A & B', 'A, B & C' — joint holders of a fielding honour. Ties at one
// catch are ordinary in a 12-a-side game, so the card names everyone on the
// mark rather than whichever row came back first.
function joinNames(names) {
  const list = (names || []).filter(Boolean);
  if (list.length <= 1) return list[0] || '—';
  return list.slice(0, -1).join(', ') + ' & ' + list[list.length - 1];
}

function ordinalWicket(n) {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ─── UCCPartnershipsTab ────────────────────────────────────
// Shows partnerships per innings. For imported matches, reads from the
// `partnerships` array in the payload (from the partnerships table). For
// LIVE matches, the array isn't present — we compute on-the-fly from the
// ball-by-ball feed (group consecutive balls by the {striker,non_striker}
// pair, until a wicket breaks the pair).
function UCCPartnershipsTab({ innings, partnerships, balls, captaincy, match }) {
  // Innings toggle: default to innings 1.
  const [activeInn, setActiveInn] = useState(1);
  // Use provided partnerships if available (imported matches); otherwise
  // derive from the live ball-by-ball feed.
  const groups = useMemo(() => {
    if (Array.isArray(partnerships) && partnerships.length > 0) {
      // Group by innings, in source order.
      const byInn = new Map();
      partnerships.forEach(p => {
        const inn = Number(p.innings) || 1;
        if (!byInn.has(inn)) byInn.set(inn, []);
        byInn.get(inn).push(p);
      });
      return Array.from(byInn.entries()).map(([inn, rows]) => ({
        innings_number: inn,
        rows: rows.map(r => ({
          player1_name: r.player1_name || '—',
          player2_name: r.player2_name || '—',
          runs:         Number(r.total_runs) || 0,
          balls:        Number(r.balls) || 0,
          wicket_number: r.wicket_number != null ? Number(r.wicket_number) : null,
          p1_runs:      Number(r.p1_runs) || 0,
          p2_runs:      Number(r.p2_runs) || 0,
        })),
      }));
    }

    // Live derivation from balls
    const byInn = new Map();
    if (!Array.isArray(balls) || balls.length === 0) return [];

    // Group balls by innings_id, preserving order.
    const ballsByInn = new Map();
    balls.forEach(b => {
      if (!ballsByInn.has(b.innings_id)) ballsByInn.set(b.innings_id, []);
      ballsByInn.get(b.innings_id).push(b);
    });
    const inningsList = (innings || []).slice().sort((a, b) => a.innings_number - b.innings_number);

    inningsList.forEach(inn => {
      const myBalls = ballsByInn.get(inn.id) || [];
      const rows = [];
      let cur = null;
      let wktNo = 0;
      function pairKey(s, n) {
        return [s, n].filter(Boolean).sort().join('|');
      }
      myBalls.forEach(b => {
        const key = pairKey(b.striker_id, b.non_striker_id);
        if (!cur || cur.key !== key) {
          if (cur) rows.push(cur);
          cur = {
            key,
            player1_name: b.striker_name     || '—',
            player2_name: b.non_striker_name || '—',
            runs: 0, balls: 0,
            p1_runs: 0, p2_runs: 0,
            wicket_number: null,
          };
        }
        if (b.is_legal) cur.balls += 1;
        const r = Number(b.runs_off_bat) || 0;
        cur.runs += r + (Number(b.wides) || 0) + (Number(b.noballs) || 0) + (Number(b.byes) || 0) + (Number(b.legbyes) || 0);
        // Track per-batter bat runs only.
        if (b.striker_id && b.striker_name === cur.player1_name) cur.p1_runs += r;
        else                                                      cur.p2_runs += r;
        if (b.dismissal_type) {
          wktNo += 1;
          cur.wicket_number = wktNo;
          rows.push(cur);
          cur = null;
        }
      });
      if (cur) rows.push(cur);
      if (rows.length > 0) {
        byInn.set(inn.innings_number, rows);
      }
    });

    return Array.from(byInn.entries()).map(([inn, rows]) => ({
      innings_number: inn, rows,
    }));
  }, [partnerships, balls, innings]);

  if (groups.length === 0) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <InningsToggle innings={innings} value={activeInn} onChange={setActiveInn} />
        <div className="card"><div className="card__body" style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
          No partnership data available.
        </div></div>
      </div>
    );
  }

  const visibleGroups = groups.filter(g => g.innings_number === activeInn);

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xs)' }}>
      <InningsToggle innings={innings} value={activeInn} onChange={setActiveInn} />
      {visibleGroups.length === 0 && (
        <div className="card mt-sm"><div className="card__body" style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
          No partnerships in this innings.
        </div></div>
      )}
      {visibleGroups.map(g => {
        const inn = (innings || []).find(i => i.innings_number === g.innings_number);
        const teamName = inn?.batting_team || `Innings ${g.innings_number}`;
        const sorted = g.rows.slice().sort((a, b) => {
          // Show in batting order: by wicket_number ascending (unbroken at end).
          if (a.wicket_number != null && b.wicket_number != null) return a.wicket_number - b.wicket_number;
          if (a.wicket_number == null) return 1;
          if (b.wicket_number == null) return -1;
          return 0;
        });
        return (
          <div key={g.innings_number} className="card mb-sm">
            <div style={{ padding: '8px 12px', background: '#0891b2', borderRadius: '10px 10px 0 0' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#cffafe', fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.06em' }}>
                🤝 PARTNERSHIPS — {teamName.toUpperCase()}
              </span>
            </div>
            <div>
              {sorted.map((p, i) => (
                <div key={i} style={{
                  padding: '8px 12px',
                  borderBottom: i === sorted.length - 1 ? 'none' : '1px solid #f1f5f9',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: i % 2 === 0 ? 'white' : '#fafbfc',
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.66rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                      {p.wicket_number != null ? `${ordinalWicket(p.wicket_number)} wicket` : 'Unbroken'}
                    </div>
                    <div style={{ fontWeight: 600, color: '#1E3A6E', fontSize: '0.85rem', lineHeight: 1.3 }}>
                      {p.player1_name}<span style={{ color: '#64748b', fontWeight: 500 }}> ({p.p1_runs})</span>
                      <span style={{ color: '#9ca3af', fontWeight: 500 }}> & </span>
                      {p.player2_name}<span style={{ color: '#64748b', fontWeight: 500 }}> ({p.p2_runs})</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem', color: '#1E3A6E' }}>
                      {p.runs}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                      ({p.balls} {p.balls === 1 ? 'ball' : 'balls'})
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '6px 12px 8px', fontSize: '0.66rem', color: '#94a3b8', fontStyle: 'italic', borderTop: '1px solid #f1f5f9' }}>
              * Partnership runs include extras conceded during the partnership.
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MemberStatsPage;
